import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Search,
  ArrowLeft,
  Edit,
  Trash2,
  Folder,
  Upload,
  Play,
  Pause,
  Square,
  X,
  Check,
  Loader2,
  Music,
  UploadCloud,
  FolderPlus,
  ChevronRight,
  Clock,
  Grid3X3,
  List,
  ArrowUpDown,
  Mic,
  FileAudio,
  AlertCircle,
  Zap,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Activity,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Settings,
  Volume2,
  Wand2,
  Layers,
  Globe,
  AlertTriangle,
  RotateCcw,
  Save,
  Download,
  Sparkles
} from 'lucide-react'
import { API_BASE_URL, changeLogApi, voiceSetApi, campaignApi } from '../services/api'
import AudioWaveform from '../components/AudioWaveform'
import { useAuth } from '../context/AuthContext'
import './VoiceSets.css'

// Toast Notification Component
const Toast = ({ toasts, removeToast }) => (
  <div className="vs-toast-container" style={{
    position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '400px'
  }}>
    <AnimatePresence>
      {toasts.map(toast => (
        <motion.div
          key={toast.id}
          className={`vs-toast vs-toast-${toast.type}`}
          initial={{ opacity: 0, x: 100, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 100, scale: 0.9 }}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            background: toast.type === 'success' ? '#22c55e' : toast.type === 'error' ? '#ef4444' : toast.type === 'warning' ? '#f59e0b' : '#3b82f6',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            cursor: 'pointer'
          }}
          onClick={() => removeToast(toast.id)}
        >
          {toast.type === 'success' && <CheckCircle2 size={18} />}
          {toast.type === 'error' && <XCircle size={18} />}
          {toast.type === 'warning' && <AlertTriangle size={18} />}
          {toast.type === 'info' && <AlertCircle size={18} />}
          <span style={{ flex: 1 }}>{toast.message}</span>
          <X size={14} style={{ opacity: 0.7 }} />
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
)

const ITEMS_PER_PAGE = 50

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } }
}

// Default ElevenLabs model + emotion settings (mirrors the backend global config)
const EMOTION_DEFAULTS = {
  model_id: 'eleven_turbo_v2_5',
  stability: 0.70,
  similarity_boost: 0.85,
  style: 0.20,
  speed: 1.0,
  use_speaker_boost: true
}

const ELEVENLABS_MODELS_FALLBACK = [
  { id: 'eleven_turbo_v2_5', name: 'Turbo v2.5 - fast, low latency', supports_tags: false },
  { id: 'eleven_multilingual_v2', name: 'Multilingual v2 - balanced, natural', supports_tags: false },
  { id: 'eleven_v3', name: 'Eleven v3 - most expressive, supports [audio tags]', supports_tags: true }
]

const parseVoiceSettings = (json) => {
  if (!json) return { ...EMOTION_DEFAULTS }
  try { return { ...EMOTION_DEFAULTS, ...(JSON.parse(json) || {}) } }
  catch (e) { return { ...EMOTION_DEFAULTS } }
}

// Emotion & Realism control panel - reused by the Voice Set modal and Clone modal
function EmotionPanel({ settings, onChange, models, onPreview, previewing, canPreview }) {
  const s = { ...EMOTION_DEFAULTS, ...(settings || {}) }
  const modelList = (models && models.length) ? models : ELEVENLABS_MODELS_FALLBACK
  const activeModel = modelList.find(m => m.id === s.model_id)
  const set = (k, v) => onChange({ ...s, [k]: v })

  const slider = (label, key, min, max, step, leftHint, rightHint) => (
    <div style={{ marginBottom: 12 }} key={key}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <strong>{Number(s[key]).toFixed(2)}</strong>
      </div>
      <input type="range" min={min} max={max} step={step} value={s[key]}
        onChange={e => set(key, parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: '#8b5cf6' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, opacity: 0.55 }}>
        <span>{leftHint}</span><span>{rightHint}</span>
      </div>
    </div>
  )

  return (
    <div style={{ border: '1px solid rgba(139,92,246,0.3)', borderRadius: 8, padding: '12px 14px', background: 'rgba(139,92,246,0.05)', marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
        <Sparkles size={15} /> Emotion &amp; Realism
      </div>

      <div className="input-group">
        <label className="input-label">Model</label>
        <select className="input" value={s.model_id} onChange={e => set('model_id', e.target.value)}>
          {modelList.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {activeModel?.supports_tags && (
        <div style={{ fontSize: 11, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 6, padding: '6px 8px', marginBottom: 10 }}>
          Tip: with Eleven v3 you can write emotion cues right in the variation text - e.g. [excited], [whispers], [laughs], [sad].
        </div>
      )}

      {slider('Stability', 'stability', 0, 1, 0.05, 'Expressive / emotional', 'Stable / consistent')}
      {slider('Similarity', 'similarity_boost', 0, 1, 0.05, 'Looser', 'Close to original')}
      {slider('Style', 'style', 0, 1, 0.05, 'Neutral', 'Exaggerated')}
      {slider('Speed', 'speed', 0.7, 1.2, 0.05, 'Slower', 'Faster')}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={!!s.use_speaker_boost} onChange={e => set('use_speaker_boost', e.target.checked)} />
        Speaker boost (sharpen similarity to the voice)
      </label>

      {onPreview && (
        <button type="button" className="btn btn-secondary" style={{ width: '100%' }}
          onClick={onPreview} disabled={previewing || !canPreview}
          title={canPreview ? 'Generate a sample with these settings' : 'Select an ElevenLabs voice first'}>
          {previewing ? <Loader2 size={14} className="spin" /> : <Volume2 size={14} />}
          {previewing ? 'Generating preview...' : 'Preview with these settings'}
        </button>
      )}
    </div>
  )
}

function VoiceSets() {
  // Existing state
  const [voiceSets, setVoiceSets] = useState([])
  const [categories, setCategories] = useState([])
  const [categoryFiles, setCategoryFiles] = useState([])
  const [selectedVoiceSet, setSelectedVoiceSet] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [view, setView] = useState('list') // 'list', 'categories', 'files', 'global-categories'
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [voiceSetSearch, setVoiceSetSearch] = useState('')
  const [categorySearch, setCategorySearch] = useState('')
  const [fileSearch, setFileSearch] = useState('')
  const [genderFilter, setGenderFilter] = useState('all') // 'all' | 'male' | 'female'
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalMode, setModalMode] = useState('add')
  const [currentItem, setCurrentItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ total: 0, processed: 0, categories: 0, files: 0, preview: {} })
  const [pendingFiles, setPendingFiles] = useState([])
  const [bulkUploading, setBulkUploading] = useState(false)
  const [playingFile, setPlayingFile] = useState(null)
  const [playingCategoryId, setPlayingCategoryId] = useState(null)
  const [audioProgress, setAudioProgress] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const [audioError, setAudioError] = useState(null)
  const [audioLoading, setAudioLoading] = useState(false)
  const audioRef = useRef(new Audio())
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const [metadataStatus, setMetadataStatus] = useState(null)
  const [metadataGenerating, setMetadataGenerating] = useState(false)
  const metadataPollingRef = useRef(null)
  const [expandedFile, setExpandedFile] = useState(null)

  // New state for ElevenLabs and global categories
  const [globalCategories, setGlobalCategories] = useState([])
  const [categoryUsageFilter, setCategoryUsageFilter] = useState('all') // 'all' | 'used' | 'unused'
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => new Set())
  const [globalCategorySearch, setGlobalCategorySearch] = useState('')
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [generationProgress, setGenerationProgress] = useState(null)
  const [testingVoice, setTestingVoice] = useState(false)
  const [variations, setVariations] = useState([{ male: '', female: '' }, { male: '', female: '' }, { male: '', female: '' }])
  const [sameForBoth, setSameForBoth] = useState(true)
  const [availableVoices, setAvailableVoices] = useState([])
  // Voice cloning + emotion control
  const [availableModels, setAvailableModels] = useState(ELEVENLABS_MODELS_FALLBACK)
  const [cloneFiles, setCloneFiles] = useState([])
  const [cloning, setCloning] = useState(false)
  const [previewingEmotion, setPreviewingEmotion] = useState(false)
  // Create Voice Set (MiniMax) — upload sample + optional full-avatar-from-campaign
  const [vsSampleFile, setVsSampleFile] = useState(null)
  const [vsFullAvatar, setVsFullAvatar] = useState(false)
  const [vsCampaignId, setVsCampaignId] = useState('')
  const [campaignsList, setCampaignsList] = useState([])
  const [vsCreateStatus, setVsCreateStatus] = useState('')
  // Script Studio (free-form text -> audio in a chosen voice)
  const [scriptText, setScriptText] = useState('')
  const [scriptVoiceSetId, setScriptVoiceSetId] = useState('')
  const [scriptSettings, setScriptSettings] = useState({ ...EMOTION_DEFAULTS })
  const [scriptGenerating, setScriptGenerating] = useState(false)
  const [scriptAudio, setScriptAudio] = useState(null)

  // MiniMax: permanent cloned voice per set + script generation (own voices only)
  const [mmModal, setMmModal] = useState(null)   // { vs, mode: 'clone' | 'generate' }
  const [mmFile, setMmFile] = useState(null)
  const [mmReplace, setMmReplace] = useState(false)
  const [mmText, setMmText] = useState('')
  const [mmCategory, setMmCategory] = useState('')
  const [mmBusy, setMmBusy] = useState(false)
  const [mmResults, setMmResults] = useState([])
  const generationPollingRef = useRef(null)

  // Preview state for testing variations before generating
  const [previewVoiceId, setPreviewVoiceId] = useState('')
  const [previewGender, setPreviewGender] = useState('female')
  const [previews, setPreviews] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(null)
  // Inline voice cloning + emotion inside the Create Category modal
  const [clonedVoices, setClonedVoices] = useState([])
  const [previewSettings, setPreviewSettings] = useState({ ...EMOTION_DEFAULTS })
  const [catClone, setCatClone] = useState({ open: false, files: [], name: '', gender: 'female', removeNoise: true, cloning: false })
  // Upload-files-only mode for Create Category: skip ElevenLabs entirely,
  // copy the user's audio files into every voice set's folder for this category.
  const [catUpload, setCatUpload] = useState({ files: [], mode: 'existing', target_voice_set_id: '', new_voice_set_name: '' })
  const [voiceTab, setVoiceTab] = useState('upload') // 'upload' (Existing/Clone tabs removed — MiniMax-only)
  const [regeneratingFile, setRegeneratingFile] = useState(null)
  // Edit-script modal (per clip)
  const [editScriptFile, setEditScriptFile] = useState(null)
  const [editScriptText, setEditScriptText] = useState('')
  const [savingScript, setSavingScript] = useState(false)
  const [categoryVariations, setCategoryVariations] = useState(null)
  const [generatingVariation, setGeneratingVariation] = useState(null)
  const [generatingAll, setGeneratingAll] = useState(false)
  const [generatingAllCategories, setGeneratingAllCategories] = useState(false)
  const previewAudioRef = useRef(new Audio())

  // Toast notifications
  const [toasts, setToasts] = useState([])
  const toastIdRef = useRef(0)

  // Change log state
  const [recentChanges, setRecentChanges] = useState([])
  const [changesLoading, setChangesLoading] = useState(false)
  const { user } = useAuth()

  const toast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastIdRef.current
    setToasts(prev => [...prev, { id, message, type }])
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration)
    }
    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Sync status
  const [syncStatus, setSyncStatus] = useState(null)
  const [autoFixing, setAutoFixing] = useState(false)

  // Variation editing
  const [editingVariation, setEditingVariation] = useState(null)
  const [editedText, setEditedText] = useState('')
  const [savingOverride, setSavingOverride] = useState(false)
  const [expandedVariation, setExpandedVariation] = useState(null)
  const [playingVariation, setPlayingVariation] = useState(null)
  const [variationCurrentTime, setVariationCurrentTime] = useState(0)
  const [variationDuration, setVariationDuration] = useState(0)
  const [variationAudioUrl, setVariationAudioUrl] = useState(null)
  const variationAudioRef = useRef(new Audio())

  // Fetch recent changes for voice sets and categories
  const fetchRecentChanges = useCallback(async () => {
    try {
      setChangesLoading(true)
      const [vsChanges, catChanges] = await Promise.all([
        changeLogApi.getRecent('voice_set', 10),
        changeLogApi.getRecent('voice_category', 10)
      ])
      // Combine and sort by date
      const allChanges = [
        ...(vsChanges.data.entries || []),
        ...(catChanges.data.entries || [])
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 15)
      setRecentChanges(allChanges)
    } catch (err) {
      console.error('Error fetching recent changes:', err)
    } finally {
      setChangesLoading(false)
    }
  }, [])

  // Variation audio player setup
  useEffect(() => {
    const audio = variationAudioRef.current
    const handleTimeUpdate = () => setVariationCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => setVariationDuration(audio.duration)
    const handleEnded = () => {
      setPlayingVariation(null)
      setVariationCurrentTime(0)
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  // Audio player setup
  useEffect(() => {
    const audio = audioRef.current
    const handleTimeUpdate = () => setAudioProgress(audio.currentTime)
    const handleLoadedMetadata = () => { setAudioDuration(audio.duration); setAudioLoading(false) }
    const handleEnded = () => { setPlayingFile(null); setPlayingCategoryId(null); setAudioProgress(0) }
    const handleError = () => {
      // Ignore errors when src is empty (happens during cleanup)
      if (!audio.src || audio.src === window.location.href) return
      setAudioError('Failed to load audio')
      setPlayingFile(null)
      setPlayingCategoryId(null)
      setAudioLoading(false)
    }
    const handleCanPlay = () => { setAudioLoading(false); setAudioError(null) }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('canplay', handleCanPlay)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.pause()
      if (metadataPollingRef.current) clearInterval(metadataPollingRef.current)
      if (generationPollingRef.current) clearInterval(generationPollingRef.current)
    }
  }, [])

  useEffect(() => { loadVoiceSets(); loadAvailableVoices(); loadSyncStatus(); fetchRecentChanges() }, [fetchRecentChanges])

  useEffect(() => {
    if (selectedVoiceSet?.id && view === 'categories') loadMetadataStatus(selectedVoiceSet.id)
    return () => { if (metadataPollingRef.current) clearInterval(metadataPollingRef.current) }
  }, [selectedVoiceSet?.id, view])

  // Poll for generation progress
  useEffect(() => {
    pollGenerationProgress()
    return () => { if (generationPollingRef.current) clearInterval(generationPollingRef.current) }
  }, [])

  // ============================================
  // DATA LOADING
  // ============================================

  const loadVoiceSets = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets`)
      const data = await res.json()
      setVoiceSets(Array.isArray(data) ? data : [])
    } catch (error) { console.error('Failed to load voice sets:', error) }
    finally { setLoading(false) }
  }

  const loadSyncStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/sync-status`)
      const data = await res.json()
      if (data.success) {
        setSyncStatus(data)
      }
    } catch (error) { console.error('Failed to load sync status:', error) }
  }

  const autoFixVoiceSet = async (voiceSetId, generateAudio = false) => {
    setAutoFixing(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/auto-fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generate_audio: generateAudio })
      })
      const data = await res.json()

      if (data.success) {
        toast(`Created ${data.created} missing categories`, 'success')
        loadVoiceSets()
        loadSyncStatus()
        if (selectedVoiceSet?.id === voiceSetId) {
          loadCategories(voiceSetId)
        }
      } else {
        toast(data.error || 'Auto-fix failed', 'error')
      }
    } catch (error) {
      toast('Auto-fix failed: ' + error.message, 'error')
    } finally {
      setAutoFixing(false)
    }
  }

  const autoFixAll = async () => {
    setAutoFixing(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/auto-fix-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generate_audio: true })
      })
      const data = await res.json()

      if (data.success) {
        if (data.generating_missing) {
          toast('Syncing... Generating missing audio files in background', 'success')
          // Start polling for progress
          startProgressPolling()
        } else if (data.total_created > 0) {
          toast(`Created ${data.total_created} missing categories`, 'success')
        } else {
          toast('All voice sets are already in sync', 'success')
        }
        loadVoiceSets()
        loadSyncStatus()
      } else {
        toast(data.error || 'Sync failed', 'error')
      }
    } catch (error) {
      toast('Sync failed: ' + error.message, 'error')
    } finally {
      setAutoFixing(false)
    }
  }

  const loadCategories = async (voiceSetId) => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/categories`)
      const data = await res.json()
      setCategories(Array.isArray(data) ? data : [])
    } catch (error) { console.error('Failed to load categories:', error) }
    finally { setLoading(false) }
  }

  const loadCategoryFiles = async (voiceSetId, categoryId) => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/files-with-metadata`)
      const data = await res.json()
      if (data.success && Array.isArray(data.files)) {
        setCategoryFiles(data.files.filter(f => f.category_id === categoryId))
      } else {
        const res2 = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/categories/${categoryId}/files`)
        const data2 = await res2.json()
        setCategoryFiles(Array.isArray(data2) ? data2 : [])
      }
    } catch (error) { console.error('Failed to load files:', error) }
    finally { setLoading(false) }
  }

  const loadGlobalCategories = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/global-categories`)
      const data = await res.json()
      if (data.success) {
        setGlobalCategories(data.categories || [])
      }
    } catch (error) { console.error('Failed to load global categories:', error) }
    finally { setLoading(false) }
  }

  const loadAvailableVoices = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/available-voices`)
      const data = await res.json()
      if (data.success) {
        setAvailableVoices(data.voices || [])
      }
    } catch (error) { console.error('Failed to load available voices:', error) }

    // Load supported ElevenLabs models for the Emotion & Realism panel
    try {
      const mRes = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/models`)
      const mData = await mRes.json()
      if (mData.success && Array.isArray(mData.models) && mData.models.length) {
        setAvailableModels(mData.models)
      }
    } catch (error) { console.error('Failed to load ElevenLabs models:', error) }
  }

  const loadMetadataStatus = async (voiceSetId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/metadata-status`)
      const data = await res.json()
      if (data.success) {
        setMetadataStatus(data)
        if (data.pending > 0 || data.processing > 0) startMetadataPolling(voiceSetId)
      }
    } catch (error) { console.error('Failed to load metadata status:', error) }
  }

  const startMetadataPolling = (voiceSetId) => {
    if (metadataPollingRef.current) clearInterval(metadataPollingRef.current)
    metadataPollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/metadata-status`)
        const data = await res.json()
        if (data.success) {
          setMetadataStatus(data)
          if (data.pending === 0 && data.processing === 0) {
            clearInterval(metadataPollingRef.current)
            metadataPollingRef.current = null
            setMetadataGenerating(false)
          }
        }
      } catch (error) { console.error('Polling error:', error) }
    }, 2000)
  }

  // ============================================
  // GENERATION PROGRESS POLLING
  // ============================================

  const startProgressPolling = () => {
    // Clear any existing polling first
    if (generationPollingRef.current) {
      clearInterval(generationPollingRef.current)
      generationPollingRef.current = null
    }
    // Start fresh polling
    pollGenerationProgress()
  }

  const pollGenerationProgress = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/generation-progress`)
      const data = await res.json()

      if (data.hasActiveJob) {
        setGenerationProgress(data.job)
        // Start polling if not already
        if (!generationPollingRef.current) {
          generationPollingRef.current = setInterval(async () => {
            try {
              const res2 = await fetch(`${API_BASE_URL}/voice-sets/generation-progress`)
              const data2 = await res2.json()
              if (data2.hasActiveJob) {
                setGenerationProgress(data2.job)
              } else {
                setGenerationProgress(null)
                clearInterval(generationPollingRef.current)
                generationPollingRef.current = null
                // Refresh voice sets list
                loadVoiceSets()
                if (view === 'global-categories') loadGlobalCategories()
              }
            } catch (e) { console.error('Generation polling error:', e) }
          }, 2000)
        }
      } else {
        setGenerationProgress(null)
      }
    } catch (error) { console.error('Failed to check generation progress:', error) }
  }

  // ============================================
  // STATS
  // ============================================

  const stats = useMemo(() => {
    const totalCategories = voiceSets.reduce((sum, vs) => sum + (vs.category_count || 0), 0)
    const totalFiles = voiceSets.reduce((sum, vs) => sum + (vs.file_count || 0), 0)
    const withElevenLabs = voiceSets.filter(vs => vs.elevenlabs_voice_id).length
    return { totalCategories, totalFiles, voiceSetCount: voiceSets.length, withElevenLabs }
  }, [voiceSets])

  const categoryStats = useMemo(() => {
    const totalFiles = categories.reduce((sum, cat) => sum + (cat.file_count || 0), 0)
    return { categoryCount: categories.length, totalFiles }
  }, [categories])

  // ============================================
  // FILTERING & SORTING
  // ============================================

  const filteredVoiceSets = useMemo(() => {
    let result = [...voiceSets]
    if (voiceSetSearch.trim()) {
      const search = voiceSetSearch.toLowerCase()
      result = result.filter(vs => vs.name?.toLowerCase().includes(search) || vs.description?.toLowerCase().includes(search))
    }
    if (genderFilter !== 'all') {
      result = result.filter(vs => (vs.gender || '').toLowerCase() === genderFilter)
    }
    result.sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break
        case 'categories': cmp = (a.category_count || 0) - (b.category_count || 0); break
        case 'files': cmp = (a.file_count || 0) - (b.file_count || 0); break
        case 'date': cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0); break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return result
  }, [voiceSets, voiceSetSearch, genderFilter, sortBy, sortDir])

  const filteredCategories = useMemo(() => {
    let result = [...categories]
    if (categorySearch.trim()) result = result.filter(cat => cat.name?.toLowerCase().includes(categorySearch.toLowerCase()))
    return result.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [categories, categorySearch])

  const filteredFiles = useMemo(() => {
    let result = [...categoryFiles]
    if (fileSearch.trim()) {
      const search = fileSearch.toLowerCase()
      result = result.filter(f => f.filename?.toLowerCase().includes(search) || f.display_name?.toLowerCase().includes(search) || f.transcription?.toLowerCase().includes(search))
    }
    return result
  }, [categoryFiles, fileSearch])

  const paginatedVoiceSets = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredVoiceSets.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredVoiceSets, currentPage])

  const totalPages = Math.ceil(filteredVoiceSets.length / ITEMS_PER_PAGE)

  // ============================================
  // VOICE SET CRUD
  // ============================================

  const openVoiceSetModal = (item = null) => {
    setModalType('voiceset')
    setModalMode(item ? 'edit' : 'add')
    setCurrentItem(item)
    setFormData({
      name: item?.name || '',
      description: item?.description || '',
      elevenlabs_voice_id: item?.elevenlabs_voice_id || '',
      gender: item?.gender || 'female',
      voice_settings: parseVoiceSettings(item?.voice_settings_json)
    })
    // reset MiniMax-create fields
    setVsSampleFile(null)
    setVsFullAvatar(false)
    setVsCampaignId('')
    setVsCreateStatus('')
    if (!item) {
      campaignApi.getAll().then(({ data }) => setCampaignsList(data.campaigns || [])).catch(() => {})
    }
    setShowModal(true)
  }

  const openCloneModal = () => {
    setModalType('clone-voice')
    setModalMode('add')
    setCurrentItem(null)
    setCloneFiles([])
    setFormData({
      name: '',
      description: '',
      gender: 'female',
      remove_background_noise: true,
      create_voice_set: true,
      voice_settings: { ...EMOTION_DEFAULTS }
    })
    setShowModal(true)
  }

  const cloneVoiceSubmit = async () => {
    if (!formData.name?.trim()) { toast('Enter a name for the voice', 'warning'); return }
    if (cloneFiles.length === 0) { toast('Add at least one audio sample', 'warning'); return }
    setCloning(true)
    try {
      const fd = new FormData()
      cloneFiles.forEach(f => fd.append('files', f))
      fd.append('name', formData.name.trim())
      if (formData.description?.trim()) fd.append('description', formData.description.trim())
      if (formData.gender) fd.append('gender', formData.gender)
      fd.append('remove_background_noise', formData.remove_background_noise ? 'true' : 'false')
      fd.append('create_voice_set', formData.create_voice_set ? 'true' : 'false')
      fd.append('voice_set_name', formData.name.trim())
      fd.append('voice_settings_json', JSON.stringify(formData.voice_settings || EMOTION_DEFAULTS))
      if (user?.id) fd.append('user_id', user.id)
      if (user?.username) fd.append('user_name', user.username)

      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/clone`, { method: 'POST', body: fd })
      const data = await res.json()
      if (!res.ok || !data.success) {
        toast('Clone failed: ' + (data.error || 'Unknown error'), 'error')
        return
      }
      if (data.voice_set_error) {
        toast('Voice cloned (' + data.voice_id + ') - voice set not created: ' + data.voice_set_error, 'warning')
      } else if (data.voice_set) {
        toast('Voice cloned + voice set "' + data.voice_set.name + '" created', 'success')
      } else {
        toast('Voice cloned: ' + data.voice_id, 'success')
      }
      setShowModal(false)
      loadVoiceSets()
      fetchRecentChanges()
      if (data.voice_set) setTimeout(() => selectVoiceSet(data.voice_set), 300)
    } catch (e) {
      toast('Clone failed: ' + e.message, 'error')
    } finally {
      setCloning(false)
    }
  }

  // ── MiniMax: clone (one-time) + generate (repeatable, appends) ──────────
  const openMM = (vs, mode) => {
    setMmModal({ vs, mode })
    setMmFile(null); setMmReplace(false)
    setMmText(''); setMmCategory(''); setMmResults([])
  }

  const submitMMClone = async () => {
    if (!mmFile) { toast('Choose a voice sample (10s–60s, clean, no music)', 'warning'); return }
    setMmBusy(true)
    try {
      const fd = new FormData()
      fd.append('file', mmFile)
      if (mmReplace) fd.append('replace', 'true')
      if (user?.id) fd.append('user_id', user.id)
      if (user?.username) fd.append('user_name', user.username)
      const res = await fetch(`${API_BASE_URL}/voice-sets/${mmModal.vs.id}/minimax/clone`, { method: 'POST', body: fd })
      const data = await res.json()
      if (!res.ok || !data.success) { toast('Clone failed: ' + (data.error || 'Unknown error'), 'error'); return }
      toast(`Voice cloned & saved permanently to "${mmModal.vs.name}"`, 'success')
      // voice is now permanent on this set — flip the modal to the generate step
      setMmModal(m => ({ ...m, vs: { ...m.vs, minimax_voice_id: data.minimax_voice_id }, mode: 'generate' }))
      loadVoiceSets(); fetchRecentChanges()
    } catch (e) {
      toast('Clone failed: ' + e.message, 'error')
    } finally { setMmBusy(false) }
  }

  const submitMMGenerate = async () => {
    if (!mmText.trim()) { toast('Enter the script text', 'warning'); return }
    if (!mmCategory.trim()) { toast('Enter a category name', 'warning'); return }
    setMmBusy(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${mmModal.vs.id}/minimax/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: mmText.trim(), category: mmCategory.trim() })
      })
      const data = await res.json()
      if (!res.ok || !data.success) { toast('Generate failed: ' + (data.error || 'Unknown error'), 'error'); return }
      toast(`Clip added to "${data.file.category}" (${data.file.chars_billed} chars)`, 'success')
      setMmResults(r => [{ ...data.file, text: mmText.trim() }, ...r])
      setMmText('')          // keep category so they can stack more lines in the same one
      loadVoiceSets()
    } catch (e) {
      toast('Generate failed: ' + e.message, 'error')
    } finally { setMmBusy(false) }
  }

  const openScriptStudio = () => {
    setModalType('script-studio')
    setModalMode('add')
    setCurrentItem(null)
    setScriptText('')
    setScriptAudio(null)
    const withVoice = voiceSets.filter(v => v.elevenlabs_voice_id)
    const first = withVoice[0]
    setScriptVoiceSetId(first ? String(first.id) : '')
    setScriptSettings(first ? parseVoiceSettings(first.voice_settings_json) : { ...EMOTION_DEFAULTS })
    setShowModal(true)
  }

  const pickScriptVoiceSet = (id) => {
    setScriptVoiceSetId(id)
    setScriptAudio(null)
    const vs = voiceSets.find(v => String(v.id) === String(id))
    if (vs) setScriptSettings(parseVoiceSettings(vs.voice_settings_json))
  }

  const generateScript = async () => {
    const vs = voiceSets.find(v => String(v.id) === String(scriptVoiceSetId))
    if (!vs || !vs.elevenlabs_voice_id) { toast('Pick a voice with an ElevenLabs voice configured', 'warning'); return }
    if (!scriptText.trim()) { toast('Type your script first', 'warning'); return }
    setScriptGenerating(true)
    setScriptAudio(null)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: vs.elevenlabs_voice_id,
          text: scriptText.trim(),
          model_id: scriptSettings.model_id,
          voice_settings: scriptSettings
        })
      })
      const data = await res.json()
      if (data.success && data.audio) {
        setScriptAudio(`data:audio/mpeg;base64,${data.audio}`)
        toast('Script generated', 'success')
      } else {
        toast('Generation failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (e) {
      toast('Generation failed: ' + e.message, 'error')
    } finally {
      setScriptGenerating(false)
    }
  }

  const previewEmotion = async () => {
    const voiceId = formData.elevenlabs_voice_id
    if (!voiceId) { toast('Select an ElevenLabs voice first', 'warning'); return }
    setPreviewingEmotion(true)
    try {
      const vs = formData.voice_settings || EMOTION_DEFAULTS
      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: voiceId,
          text: 'Hi there, thanks for taking my call today. How are you doing?',
          model_id: vs.model_id,
          voice_settings: vs
        })
      })
      const data = await res.json()
      if (data.success && data.audio) {
        new Audio(`data:audio/mpeg;base64,${data.audio}`).play()
        toast('Playing preview', 'success')
      } else {
        toast('Preview failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (e) {
      toast('Preview failed: ' + e.message, 'error')
    } finally {
      setPreviewingEmotion(false)
    }
  }

  const saveVoiceSet = async () => {
    if (!formData.name?.trim()) return

    // EDIT mode: keep simple metadata update.
    if (currentItem?.id) {
      setSaving(true)
      try {
        await fetch(`${API_BASE_URL}/voice-sets/${currentItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name.trim(),
            description: formData.description?.trim() || null,
            gender: formData.gender || null,
            user_id: user?.id || null, user_name: user?.username || null
          })
        })
        loadVoiceSets(); fetchRecentChanges(); setShowModal(false)
      } catch (error) { console.error('Failed to save:', error) }
      finally { setSaving(false) }
      return
    }

    // ADD mode: MiniMax — create set, clone the uploaded sample, optionally build the full avatar from a campaign.
    if (!vsSampleFile) { toast('Upload a voice sample to clone (10s–60s, clean, no music)', 'warning'); return }
    if (vsFullAvatar && !vsCampaignId) { toast('Pick a campaign for full avatar sound creation', 'warning'); return }
    setSaving(true)
    try {
      setVsCreateStatus('Creating voice set…')
      const res = await fetch(`${API_BASE_URL}/voice-sets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description?.trim() || null,
          gender: formData.gender || null,
          user_id: user?.id || null, user_name: user?.username || null
        })
      })
      const result = await res.json()
      const newId = result.id
      if (!newId) throw new Error(result.error || 'Voice set creation failed')

      setVsCreateStatus('Cloning your voice (MiniMax)…')
      const clone = await voiceSetApi.minimaxClone(newId, vsSampleFile)
      if (!clone.data?.success) throw new Error(clone.data?.error || 'Voice clone failed')

      if (vsFullAvatar && vsCampaignId) {
        const camp = campaignsList.find(c => String(c.id) === String(vsCampaignId))
        setVsCreateStatus(`Generating ${camp?.script_count || ''} script lines + building avatar…`)
        const mat = await campaignApi.materialize(vsCampaignId, {
          voice_set_id: newId, user_id: user?.id || null, user_name: user?.username || null
        })
        const d = mat.data || {}
        toast(`Voice set + avatar "${d.avatar_name}" created (${(d.generated || []).length} clips, ${d.actions_copied} actions)` +
          ((d.failed || []).length ? ` — ${d.failed.length} failed` : ''), (d.failed || []).length ? 'warning' : 'success', 7000)
      } else {
        toast(`Voice set "${formData.name.trim()}" created + voice cloned`, 'success')
      }

      setVsCreateStatus('')
      loadVoiceSets(); fetchRecentChanges(); setShowModal(false)
      setTimeout(() => selectVoiceSet({ id: newId, name: formData.name.trim() }), 300)
    } catch (error) {
      console.error('Failed to create voice set:', error)
      toast('Create failed: ' + (error.message || 'Unknown error'), 'error', 6000)
      setVsCreateStatus('')
    } finally { setSaving(false) }
  }

  const testElevenLabsVoice = async () => {
    if (!formData.elevenlabs_voice_id) {
      toast('Please select a voice first', 'warning')
      return
    }

    setTestingVoice(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: formData.elevenlabs_voice_id,
          text: 'Hello, this is a test of the voice. How does it sound?'
        })
      })

      const data = await res.json()
      if (data.success && data.audio) {
        // Play the audio
        const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`)
        audio.play()
        toast('Playing test audio', 'success')
      } else {
        toast('Failed to test voice: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      toast('Failed to test voice: ' + error.message, 'error')
    } finally {
      setTestingVoice(false)
    }
  }

  const confirmDeleteVoiceSet = (item, e) => { e?.stopPropagation(); setModalType('delete'); setCurrentItem(item); setShowModal(true) }

  const deleteVoiceSet = async () => {
    setSaving(true)
    try {
      const params = new URLSearchParams({ user_id: user?.id || '', user_name: user?.username || '' })
      await fetch(`${API_BASE_URL}/voice-sets/${currentItem.id}?${params}`, { method: 'DELETE' })
      loadVoiceSets()
      fetchRecentChanges()
      if (selectedVoiceSet?.id === currentItem.id) { setSelectedVoiceSet(null); setView('list') }
      setShowModal(false)
    } catch (error) { console.error('Failed to delete:', error) }
    finally { setSaving(false) }
  }

  const selectVoiceSet = (item) => {
    stopAudio()
    setSelectedVoiceSet(item)
    setSelectedCategory(null)
    setView('categories')
    setCategorySearch('')
    setMetadataStatus(null)
    loadCategories(item.id)
  }

  // ============================================
  // GLOBAL CATEGORIES CRUD
  // ============================================

  const openGlobalCategoryModal = (item = null) => {
    console.log('[Modal] Opening with item:', item)
    console.log('[Modal] same_for_both_genders raw value:', item?.same_for_both_genders, 'type:', typeof item?.same_for_both_genders)

    setModalType('global-category')
    setModalMode(item ? 'edit' : 'add')
    setCurrentItem(item)
    setFormData({
      name: item?.name || '',
      description: item?.description || ''
    })
    // Handle both old format (text_content) and new format (text_content_male/female)
    if (item?.variations) {
      const mappedVariations = item.variations.map(v => ({
        male: v.text_content_male || v.text_content || '',
        female: v.text_content_female || ''
      }))
      setVariations(mappedVariations.length >= 3 ? mappedVariations : [{ male: '', female: '' }, { male: '', female: '' }, { male: '', female: '' }])
      // Handle MySQL returning 0/1 - explicitly check for truthy 1 or true
      const sameForBothValue = item.same_for_both_genders === 1 || item.same_for_both_genders === true
      console.log('[Modal] Setting sameForBoth to:', sameForBothValue)
      setSameForBoth(sameForBothValue)
    } else {
      setVariations([{ male: '', female: '' }, { male: '', female: '' }, { male: '', female: '' }])
      setSameForBoth(true)
    }
    setPreviews([])
    setPreviewVoiceId('')
    setPreviewGender('female')
    setPreviewSettings({ ...EMOTION_DEFAULTS })
    setCatClone({ open: false, files: [], name: '', gender: 'female', removeNoise: true, cloning: false })
    setVoiceTab('upload')
    setShowModal(true)
  }

  // Tab 2: clone the uploaded voice (it becomes a voice set), then create the
  // category - which generates for ALL voice sets, including the new clone.
  const createCategoryWithClone = async () => {
    if (!formData.name?.trim()) { toast('Enter a category name', 'warning'); return }
    if (variations.filter(v => v.male?.trim()).length < 3) { toast('Add at least 3 text variations', 'warning'); return }
    if (!catClone.name.trim()) { toast('Enter a name for the cloned voice', 'warning'); return }
    if (catClone.files.length === 0) { toast('Upload an audio sample to clone', 'warning'); return }
    setSaving(true)
    try {
      const fd = new FormData()
      catClone.files.forEach(f => fd.append('files', f))
      fd.append('name', catClone.name.trim())
      fd.append('gender', catClone.gender)
      fd.append('remove_background_noise', catClone.removeNoise ? 'true' : 'false')
      fd.append('create_voice_set', 'true')
      fd.append('voice_set_name', catClone.name.trim())
      fd.append('voice_settings_json', JSON.stringify(previewSettings))
      if (user?.id) fd.append('user_id', user.id)
      if (user?.username) fd.append('user_name', user.username)

      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/clone`, { method: 'POST', body: fd })
      const data = await res.json()
      if (!res.ok || !data.success) {
        toast('Clone failed: ' + (data.error || 'Unknown error'), 'error')
        setSaving(false)
        return
      }
      if (data.voice_set_error) {
        toast('Clone failed - voice set not created: ' + data.voice_set_error, 'error')
        setSaving(false)
        return
      }
      toast('Voice cloned - creating category...', 'success')
      loadVoiceSets()
      // Create the category - it generates for all voice sets incl. the new clone
      await saveGlobalCategory()
    } catch (e) {
      toast('Failed: ' + e.message, 'error')
      setSaving(false)
    }
  }

  // Test variations before creating - generates preview audio
  const testVariationPreviews = async () => {
    if (!previewVoiceId) {
      toast('Please select a voice to test', 'warning')
      return
    }

    // Get the appropriate text based on gender selection
    const textsToTest = variations.map(v => {
      if (sameForBoth) return v.male
      return previewGender === 'female' ? (v.female || v.male) : v.male
    }).filter(t => t?.trim())

    if (textsToTest.length < 3) {
      toast('Please enter at least 3 text variations', 'warning')
      return
    }

    setPreviewLoading(true)
    setPreviews([])

    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/elevenlabs/test-variations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: previewVoiceId,
          variations: textsToTest.map((text, i) => ({ text, variation_number: i + 1 })),
          model_id: previewSettings.model_id,
          voice_settings: previewSettings
        })
      })

      const result = await res.json()

      if (result.success) {
        setPreviews(result.variations)
      } else {
        toast('Preview failed: ' + (result.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('Preview error:', error)
      toast('Failed to generate previews: ' + error.message, 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  // Play a preview audio
  const playPreview = (preview) => {
    if (previewPlaying === preview.variation_number) {
      previewAudioRef.current.pause()
      setPreviewPlaying(null)
      return
    }

    previewAudioRef.current.pause()
    previewAudioRef.current.src = `data:audio/mpeg;base64,${preview.audio}`
    previewAudioRef.current.onended = () => setPreviewPlaying(null)
    previewAudioRef.current.play()
    setPreviewPlaying(preview.variation_number)
  }

  const saveGlobalCategory = async () => {
    if (!formData.name?.trim()) return

    // Validate - need at least 3 variations with male text
    const validVariations = variations.filter(v => v.male?.trim())
    if (validVariations.length < 3) {
      toast('Please enter at least 3 text variations', 'warning')
      return
    }

    setSaving(true)
    try {
      const data = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        same_for_both_genders: sameForBoth,
        variations: validVariations.map(v => ({
          text_male: v.male?.trim() || '',
          text_female: sameForBoth ? null : (v.female?.trim() || null)
        })),
        user_id: user?.id || null,
        user_name: user?.username || null
      }

      console.log('[Save] Sending data:', JSON.stringify(data, null, 2))

      if (currentItem?.id) {
        const res = await fetch(`${API_BASE_URL}/voice-sets/global-categories/${currentItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        })
        const result = await res.json()
        console.log('[Save] PUT response:', result)
        toast('Category updated', 'success')
      } else {
        const res = await fetch(`${API_BASE_URL}/voice-sets/global-categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        })
        const result = await res.json()
        console.log('[Save] POST response:', result)

        // If generating, start polling
        if (result.generating) {
          pollGenerationProgress()
        }
        toast('Category created', 'success')
      }

      loadGlobalCategories()
      fetchRecentChanges()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to save:', error)
      toast('Failed to save category: ' + error.message, 'error')
    }
    finally { setSaving(false) }
  }

  // Create a per-voice-set category from uploaded audio files (no ElevenLabs).
  // User picks either an existing voice set or a new voice set name.
  const createCategoryWithUpload = async () => {
    if (!formData.name?.trim()) return
    if (catUpload.files.length === 0) { toast('Pick at least 1 audio file to upload', 'warning'); return }
    if (catUpload.mode === 'existing' && !catUpload.target_voice_set_id) {
      toast('Pick a voice set to add to', 'warning'); return
    }
    if (catUpload.mode === 'new' && !catUpload.new_voice_set_name?.trim()) {
      toast('Enter a name for the new voice set', 'warning'); return
    }

    setSaving(true)
    try {
      const fd = new FormData()
      fd.append('name', formData.name.trim())
      if (formData.description?.trim()) fd.append('description', formData.description.trim())
      fd.append('target_mode', catUpload.mode)
      if (catUpload.mode === 'existing') {
        fd.append('target_voice_set_id', catUpload.target_voice_set_id)
      } else {
        fd.append('new_voice_set_name', catUpload.new_voice_set_name.trim())
      }
      if (user?.id) fd.append('user_id', user.id)
      if (user?.username) fd.append('user_name', user.username)
      catUpload.files.forEach(f => fd.append('files', f))

      const res = await fetch(`${API_BASE_URL}/voice-sets/global-categories/upload`, {
        method: 'POST',
        body: fd,
      })
      const result = await res.json()
      if (!res.ok || !result.success) {
        toast('Upload failed: ' + (result.error || 'Unknown error'), 'error')
        return
      }

      const vsBit = result.voice_set?.created ? `new voice set "${result.voice_set.name}"` : `voice set "${result.voice_set.name}"`
      const replaceBit = result.category?.replaced ? ' (replaced existing)' : ''
      toast(`${catUpload.files.length} file(s) uploaded to ${vsBit}${replaceBit}`, 'success')
      setCatUpload({ files: [], mode: 'existing', target_voice_set_id: '', new_voice_set_name: '' })
      loadVoiceSets()
      loadGlobalCategories()
      fetchRecentChanges()
      setShowModal(false)
    } catch (error) {
      console.error('Failed to upload category:', error)
      toast('Failed to upload category: ' + error.message, 'error')
    }
    finally { setSaving(false) }
  }

  const confirmDeleteGlobalCategory = (item, e) => {
    e?.stopPropagation()
    setModalType('deleteGlobalCategory')
    setCurrentItem(item)
    setShowModal(true)
  }

  const deleteGlobalCategory = async () => {
    setSaving(true)
    try {
      const params = new URLSearchParams({ user_id: user?.id || '', user_name: user?.username || '' })
      await fetch(`${API_BASE_URL}/voice-sets/global-categories/${currentItem.id}?${params}`, { method: 'DELETE' })
      loadGlobalCategories()
      fetchRecentChanges()
      setShowModal(false)
    } catch (error) { console.error('Failed to delete:', error) }
    finally { setSaving(false) }
  }

  const toggleCategorySelection = (id) => {
    setSelectedCategoryIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearCategorySelection = () => setSelectedCategoryIds(new Set())

  const selectAllVisibleCategories = (visibleCats) => {
    setSelectedCategoryIds(new Set(visibleCats.map(c => c.id)))
  }

  const bulkDeleteGlobalCategories = async () => {
    if (selectedCategoryIds.size === 0) return
    if (!window.confirm(`Delete ${selectedCategoryIds.size} categories from all voice sets? This cannot be undone.`)) return
    setBulkDeleting(true)
    const params = new URLSearchParams({ user_id: user?.id || '', user_name: user?.username || '' })
    const ids = Array.from(selectedCategoryIds)
    let ok = 0, failed = 0
    for (const id of ids) {
      try {
        const res = await fetch(`${API_BASE_URL}/voice-sets/global-categories/${id}?${params}`, { method: 'DELETE' })
        if (res.ok) ok++; else failed++
      } catch (e) { failed++ }
    }
    setBulkDeleting(false)
    clearCategorySelection()
    loadGlobalCategories()
    fetchRecentChanges()
    if (failed > 0) toast(`Deleted ${ok}, failed ${failed}`, 'warning')
    else toast(`Deleted ${ok} categories`, 'success')
  }

  // ============================================
  // LOCAL CATEGORY CRUD (existing)
  // ============================================

  const openCategoryModal = (item = null, e) => { e?.stopPropagation(); setModalType('category'); setModalMode(item ? 'edit' : 'add'); setCurrentItem(item); setFormData({ name: item?.name || '', description: item?.description || '' }); setShowModal(true) }

  const saveCategory = async () => {
    if (!formData.name?.trim()) return
    setSaving(true)
    try {
      const data = { name: formData.name.trim().toLowerCase().replace(/\s+/g, '_'), description: formData.description?.trim() || null }
      if (currentItem?.id) {
        await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${currentItem.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
      } else {
        await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
      }
      loadCategories(selectedVoiceSet.id)
      setShowModal(false)
    } catch (error) { console.error('Failed to save:', error); alert('Error: Category name may already exist') }
    finally { setSaving(false) }
  }

  const confirmDeleteCategory = (item, e) => { e?.stopPropagation(); setModalType('deleteCategory'); setCurrentItem(item); setShowModal(true) }

  const deleteCategory = async () => {
    setSaving(true)
    try {
      await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${currentItem.id}`, { method: 'DELETE' })
      loadCategories(selectedVoiceSet.id)
      if (selectedCategory?.id === currentItem.id) { setSelectedCategory(null); setView('categories') }
      setShowModal(false)
    } catch (error) { console.error('Failed to delete:', error) }
    finally { setSaving(false) }
  }

  const selectCategory = (item) => {
    stopAudio()
    setSelectedCategory(item)
    setView('files')
    setFileSearch('')
    setExpandedFile(null)
    setCategoryVariations(null)
    loadCategoryFiles(selectedVoiceSet.id, item.id)
    loadCategoryVariations(selectedVoiceSet.id, item.id)
  }

  const loadCategoryVariations = async (voiceSetId, categoryId) => {
    try {
      console.log(`[Variations] Loading for voiceSet=${voiceSetId}, category=${categoryId}`)
      const res = await fetch(`${API_BASE_URL}/voice-sets/${voiceSetId}/categories/${categoryId}/variations`)
      const data = await res.json()
      console.log('[Variations] Response:', data)
      if (data.success) {
        setCategoryVariations(data)
      } else {
        console.warn('[Variations] API returned success=false:', data)
        setCategoryVariations(null)
      }
    } catch (error) {
      console.error('Failed to load variations:', error)
      setCategoryVariations(null)
    }
  }

  const generateVariation = async (variationNumber, customText = null) => {
    if (!selectedVoiceSet?.elevenlabs_voice_id) {
      toast('Voice set has no ElevenLabs voice configured', 'error')
      return
    }

    setGeneratingVariation(variationNumber)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/generate-variation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            variation_number: variationNumber,
            text: customText
          })
        }
      )
      const data = await res.json()

      if (data.success) {
        toast(`Generated variation ${variationNumber}`, 'success')
        loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
      } else {
        toast('Generation failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('Failed to generate variation:', error)
      toast('Failed to generate: ' + error.message, 'error')
    } finally {
      setGeneratingVariation(null)
    }
  }

  const generateAllMissing = async () => {
    if (!selectedVoiceSet?.elevenlabs_voice_id) {
      toast('Voice set has no ElevenLabs voice configured', 'error')
      return
    }

    const missing = categoryVariations?.variations?.filter(v => !v.has_file)?.length || 0
    if (missing === 0) {
      toast('All variations already generated', 'info')
      return
    }

    setGeneratingAll(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/generate-all`,
        { method: 'POST' }
      )
      const data = await res.json()

      if (data.success) {
        toast(`Generated: ${data.generated}, Failed: ${data.failed}`, data.failed > 0 ? 'warning' : 'success')
        loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
      } else {
        toast('Generation failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('Failed to generate all:', error)
      toast('Failed: ' + error.message, 'error')
    } finally {
      setGeneratingAll(false)
    }
  }

  // Generate all missing files for entire voice set (all categories)
  const generateAllCategories = async () => {
    if (!selectedVoiceSet?.elevenlabs_voice_id) {
      toast('Voice set has no ElevenLabs voice configured', 'error')
      return
    }

    if (!window.confirm(`Generate all missing audio files for "${selectedVoiceSet.name}"?\n\nThis will generate files for ALL categories using ElevenLabs.\nGeneration runs in the background - check server logs for progress.`)) {
      return
    }

    setGeneratingAllCategories(true)

    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/generate-all-categories`,
        { method: 'POST' }
      )
      const data = await res.json()

      if (data.success) {
        if (data.total_missing === 0) {
          toast('All files already exist!', 'info')
        } else {
          // Generation started in background
          toast(`Started: ${data.total_missing} files queued. Est. ${data.estimated_time}. Refresh page to see progress.`, 'success', 10000)
        }
      } else {
        toast('Generation failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('Failed to generate all categories:', error)
      toast('Failed: ' + error.message, 'error')
    } finally {
      setGeneratingAllCategories(false)
    }
  }

  // Save custom text override for a variation
  const saveTextOverride = async (variationNumber, customText) => {
    if (!selectedCategory?.name || !selectedVoiceSet?.id) return

    setSavingOverride(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/overrides`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category_name: selectedCategory.name,
            variation_number: variationNumber,
            custom_text: customText,
            user_id: user?.id || null,
            user_name: user?.username || null
          })
        }
      )
      const data = await res.json()

      if (data.success) {
        toast('Custom text saved', 'success')
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
        fetchRecentChanges()
        setEditingVariation(null)
        setEditedText('')
      } else {
        toast('Save failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      toast('Save failed: ' + error.message, 'error')
    } finally {
      setSavingOverride(false)
    }
  }

  // Revert to global text (delete override)
  const revertToGlobal = async (variationNumber) => {
    if (!selectedCategory?.name || !selectedVoiceSet?.id) return

    setSavingOverride(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/overrides/${encodeURIComponent(selectedCategory.name)}/${variationNumber}`,
        { method: 'DELETE' }
      )
      const data = await res.json()

      if (data.success) {
        toast('Reverted to global text', 'success')
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
      } else {
        toast('Revert failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      toast('Revert failed: ' + error.message, 'error')
    } finally {
      setSavingOverride(false)
    }
  }

  // Start editing a variation
  const startEditingVariation = (variation) => {
    setEditingVariation(variation.variation_number)
    setEditedText(variation.effective_text || variation.global_text || '')
  }

  // Cancel editing
  const cancelEditingVariation = () => {
    setEditingVariation(null)
    setEditedText('')
  }

  // Play variation audio with cache busting
  const playVariationAudio = (variation) => {
    if (!variation.file) return

    const audio = variationAudioRef.current
    const baseUrl = `${API_BASE_URL}${variation.file.download_url}`

    if (playingVariation === variation.variation_number) {
      // Toggle pause/play
      if (audio.paused) {
        audio.play()
      } else {
        audio.pause()
      }
      return
    }

    // Stop any other playing audio
    audioRef.current.pause()
    setPlayingFile(null)

    // Update waveform URL with cache-busting timestamp
    const urlWithTimestamp = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
    setVariationAudioUrl(urlWithTimestamp)
    setVariationCurrentTime(0)

    // Play audio immediately without waiting for waveform
    // Add cache-busting timestamp to avoid browser caching old audio
    audio.src = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
    audio.load()
    audio.play()
    setPlayingVariation(variation.variation_number)

    audio.onended = () => {
      setPlayingVariation(null)
      setVariationCurrentTime(0)
    }
    audio.onerror = () => {
      toast('Failed to play audio', 'error')
      setPlayingVariation(null)
    }
  }

  // Stop variation audio
  const stopVariationAudio = () => {
    variationAudioRef.current.pause()
    variationAudioRef.current.currentTime = 0
    variationAudioRef.current.src = '' // Clear src to prevent caching issues
    setPlayingVariation(null)
    setVariationCurrentTime(0)
  }

  // Seek variation audio
  const seekVariationAudio = (time) => {
    const audio = variationAudioRef.current
    if (audio.src) {
      audio.currentTime = time
      setVariationCurrentTime(time)
    }
  }

  // Toggle expanded variation
  const toggleExpandedVariation = (variationNumber) => {
    setExpandedVariation(prev => prev === variationNumber ? null : variationNumber)
  }

  // Regenerate variation audio (uses custom text if available)
  const regenerateVariationAudio = async (variation) => {
    if (!variation.file || !selectedVoiceSet?.elevenlabs_voice_id) return

    setGeneratingVariation(variation.variation_number)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/files/${variation.file.id}/regenerate`,
        { method: 'POST' }
      )
      const data = await res.json()

      if (data.success) {
        toast('Audio regenerated', 'success')
        if (playingVariation === variation.variation_number) stopVariationAudio()
        loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
      } else {
        toast('Regeneration failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      toast('Failed to regenerate: ' + error.message, 'error')
    } finally {
      setGeneratingVariation(null)
    }
  }

  // ============================================
  // FILE HANDLING (existing)
  // ============================================

  const handleFilesUpload = async (event) => {
    const files = event.target.files
    if (!files?.length) return
    setSaving(true)
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.wav')) continue
      const fd = new FormData(); fd.append('file', file)
      try { await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/files`, { method: 'POST', body: fd }) }
      catch (error) { console.error('Upload failed:', error) }
    }
    setSaving(false); loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const deleteFile = async (file, e) => {
    e?.stopPropagation()
    if (!window.confirm(`Delete "${file.display_name || file.filename}"?`)) return
    try {
      await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/files/${file.id}`, { method: 'DELETE' })
      loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
      if (playingFile === file.id) stopAudio()
    } catch (error) { console.error('Failed to delete file:', error); toast('Failed to delete file', 'error') }
  }

  const regenerateFile = async (file, e) => {
    e?.stopPropagation()
    if (!selectedVoiceSet?.elevenlabs_voice_id) {
      toast('Voice set has no ElevenLabs voice configured', 'error')
      return
    }

    setRegeneratingFile(file.id)
    try {
      const res = await fetch(
        `${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/categories/${selectedCategory.id}/files/${file.id}/regenerate`,
        { method: 'POST' }
      )
      const data = await res.json()

      if (data.success) {
        toast('Audio regenerated successfully', 'success')
        loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
        loadCategoryVariations(selectedVoiceSet.id, selectedCategory.id)
        if (playingFile === file.id) stopAudio()
      } else {
        toast('Regeneration failed: ' + (data.error || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('Failed to regenerate file:', error)
      toast('Failed to regenerate: ' + error.message, 'error')
    } finally {
      setRegeneratingFile(null)
    }
  }

  // Regenerate a clip with the set's cloned MiniMax voice (re-voices its own text, in place)
  const regenerateFileMM = async (file, e) => {
    e?.stopPropagation()
    if (!selectedVoiceSet?.minimax_voice_id) { toast('This set has no cloned voice', 'error'); return }
    if (!file.transcription?.trim()) { toast('This clip has no script text to regenerate', 'warning'); return }
    setRegeneratingFile(file.id)
    try {
      const { data } = await voiceSetApi.minimaxRegenerate(selectedVoiceSet.id, file.id)
      if (data?.success) {
        toast('Audio regenerated', 'success')
        loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
        if (playingFile === file.id) stopAudio()
      } else {
        toast('Regenerate failed: ' + (data?.error || 'Unknown error'), 'error')
      }
    } catch (err) {
      toast('Regenerate failed: ' + (err.response?.data?.error || err.message), 'error')
    } finally { setRegeneratingFile(null) }
  }

  const openEditScript = (file, e) => {
    e?.stopPropagation()
    setEditScriptFile(file)
    setEditScriptText(file.transcription || '')
  }
  // Save edited script. regenerate=true also re-voices the clip with the new text.
  const saveEditScript = async (regenerate) => {
    if (!editScriptFile) return
    const text = editScriptText.trim()
    if (!text) { toast('Script text cannot be empty', 'warning'); return }
    setSavingScript(true)
    try {
      if (regenerate) {
        if (!selectedVoiceSet?.minimax_voice_id) { toast('This set has no cloned voice to regenerate with', 'error'); setSavingScript(false); return }
        const { data } = await voiceSetApi.minimaxRegenerate(selectedVoiceSet.id, editScriptFile.id, { text })
        if (!data?.success) throw new Error(data?.error || 'Regenerate failed')
        toast('Script saved + audio regenerated', 'success')
      } else {
        const { data } = await voiceSetApi.updateFileText(selectedVoiceSet.id, selectedCategory.id, editScriptFile.id, { transcription: text })
        if (!data?.success) throw new Error(data?.error || 'Save failed')
        toast('Script text saved', 'success')
      }
      setEditScriptFile(null)
      loadCategoryFiles(selectedVoiceSet.id, selectedCategory.id)
      if (playingFile === editScriptFile.id) stopAudio()
    } catch (err) {
      toast('Save failed: ' + (err.response?.data?.error || err.message), 'error')
    } finally { setSavingScript(false) }
  }

  const handleFolderUpload = async (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return
    const wavFiles = files.filter(f => f.name.toLowerCase().endsWith('.wav'))
    if (wavFiles.length === 0) { toast('No WAV files found', 'warning'); return }
    const preview = {}
    wavFiles.forEach(file => {
      const path = file.webkitRelativePath || file.name
      const parts = path.split('/').filter(p => p)
      let categoryName = parts.length > 1 ? parts[parts.length - 2] : 'Uncategorized'
      if (!preview[categoryName]) preview[categoryName] = []
      preview[categoryName].push(file.name)
    })
    setBulkUploadProgress({ total: wavFiles.length, processed: 0, categories: Object.keys(preview).length, files: wavFiles.length, preview })
    setPendingFiles(wavFiles); setModalType('bulk'); setShowModal(true)
  }

  const startBulkUpload = async () => {
    if (!pendingFiles.length) return
    setBulkUploading(true)
    const fd = new FormData(); const filePaths = {}
    pendingFiles.forEach(file => { fd.append('files', file); filePaths[file.name] = file.webkitRelativePath || file.name })
    fd.append('filePaths', JSON.stringify(filePaths))
    try {
      await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/bulk-upload`, { method: 'POST', body: fd })
      loadCategories(selectedVoiceSet.id)
      setTimeout(() => { setShowModal(false); setBulkUploading(false); setPendingFiles([]) }, 500)
    } catch (error) { console.error('Bulk upload failed:', error); setBulkUploading(false) }
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  // ============================================
  // AUDIO PLAYBACK
  // ============================================

  const playAudio = (file) => {
    const audio = audioRef.current
    if (playingFile === file.id) { audio.paused ? audio.play() : audio.pause(); return }
    stopAudio(); setAudioLoading(true); setAudioError(null); setPlayingFile(file.id)
    // Add cache-busting timestamp to prevent playing cached old audio
    audio.src = `${API_BASE_URL}/voice-sets/files/${file.filename}?t=${Date.now()}`; audio.load()
    audio.play().catch(() => { setAudioError('Playback failed'); setPlayingFile(null); setAudioLoading(false) })
  }

  const stopAudio = () => { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current.src = ''; setPlayingFile(null); setAudioProgress(0) }
  const seekAudio = (e, file) => { if (playingFile !== file.id) return; const rect = e.currentTarget.getBoundingClientRect(); audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * audioDuration }

  // Play a category's first audio file straight from its card, without opening the folder.
  const playCategory = async (cat, e) => {
    e.stopPropagation()
    if (playingCategoryId === cat.id) { stopAudio(); setPlayingCategoryId(null); return }
    try {
      setAudioLoading(true)
      const res = await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/files-with-metadata`)
      const data = await res.json()
      const files = (data?.success && Array.isArray(data.files)) ? data.files.filter(f => f.category_id === cat.id) : []
      if (!files.length) { setAudioError(`No audio in "${cat.name}"`); setAudioLoading(false); return }
      const audio = audioRef.current
      setPlayingFile(null)
      setPlayingCategoryId(cat.id)
      audio.src = `${API_BASE_URL}/voice-sets/files/${encodeURIComponent(files[0].filename)}?t=${Date.now()}`
      audio.load()
      audio.play().catch(() => { setAudioError('Playback failed'); setPlayingCategoryId(null); setAudioLoading(false) })
    } catch (err) { setAudioError('Playback failed'); setPlayingCategoryId(null); setAudioLoading(false) }
  }

  // ============================================
  // METADATA
  // ============================================

  const generateMetadata = async (force = false) => {
    if (!selectedVoiceSet?.id) return
    setMetadataGenerating(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/generate-metadata`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ force })
      })
      const data = await res.json()
      if (data.success && data.queued > 0) {
        startMetadataPolling(selectedVoiceSet.id)
        loadMetadataStatus(selectedVoiceSet.id)
      } else { setMetadataGenerating(false); loadMetadataStatus(selectedVoiceSet.id) }
    } catch (error) { console.error('Failed to generate metadata:', error); setMetadataGenerating(false) }
  }

  const retryFailedMetadata = async () => {
    if (!selectedVoiceSet?.id) return
    setMetadataGenerating(true)
    try {
      const res = await fetch(`${API_BASE_URL}/voice-sets/${selectedVoiceSet.id}/retry-failed`, { method: 'POST' })
      const data = await res.json()
      if (data.success && data.queued > 0) { startMetadataPolling(selectedVoiceSet.id); loadMetadataStatus(selectedVoiceSet.id) }
      else { setMetadataGenerating(false) }
    } catch (error) { console.error('Failed to retry:', error); setMetadataGenerating(false) }
  }

  // ============================================
  // HELPERS
  // ============================================

  const getPhrases = (file) => { if (!file.boundaries) return []; const b = typeof file.boundaries === 'string' ? JSON.parse(file.boundaries) : file.boundaries; return b.phrases || [] }
  const getFirstPhrase = (file) => { const p = getPhrases(file); return p.length > 0 ? p[0].text : null }
  const getPhraseCount = (file) => getPhrases(file).length
  const toggleFileExpand = (fileId) => setExpandedFile(expandedFile === fileId ? null : fileId)

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
  const formatDuration = (ms) => { if (!ms) return '-'; const s = Math.round(ms / 1000); const m = Math.floor(s / 60); return m > 0 ? `${m}:${(s % 60).toString().padStart(2, '0')}` : `${s}s` }
  const formatTime = (s) => !s || !isFinite(s) ? '0:00' : `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`
  const formatTimestamp = (s) => s === undefined || s === null ? '0.00s' : `${s.toFixed(2)}s`
  const convertToSnakeCase = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
  const getVoiceSetDisplayName = () => selectedVoiceSet?.name?.replace(/[^a-zA-Z0-9]/g, '') || 'VoiceSet'

  const addVariation = () => setVariations([...variations, { male: '', female: '' }])
  const removeVariation = (index) => {
    if (variations.length <= 3) return
    setVariations(variations.filter((_, i) => i !== index))
  }
  const updateVariation = (index, field, value) => {
    const newVariations = [...variations]
    newVariations[index] = { ...newVariations[index], [field]: value }
    setVariations(newVariations)
  }

  // ============================================
  // RENDER
  // ============================================

  return (
    <motion.div className="voice-sets-page modern" variants={containerVariants} initial="hidden" animate="visible">

      {/* TOAST NOTIFICATIONS */}
      <Toast toasts={toasts} removeToast={removeToast} />

      {/* SYNC MISMATCH BANNER */}
      {syncStatus && syncStatus.mismatched_count > 0 && view === 'list' && (
        <motion.div
          className="vs-sync-banner"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: 'linear-gradient(135deg, #f59e0b20, #f97316 20)',
            border: '1px solid #f59e0b40',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
            <div>
              <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                {syncStatus.mismatched_count} voice set{syncStatus.mismatched_count > 1 ? 's' : ''} out of sync
              </span>
              <span style={{ color: 'var(--text-muted)', marginLeft: '8px', fontSize: '0.85rem' }}>
                Missing categories compared to global ({syncStatus.global_category_count} total)
              </span>
            </div>
          </div>
          <button
            className="btn btn-warning btn-sm"
            onClick={autoFixAll}
            disabled={autoFixing}
            style={{ background: '#f59e0b', color: 'white' }}
          >
            {autoFixing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
            Auto-Fix All
          </button>
        </motion.div>
      )}

      {/* GENERATION PROGRESS BAR */}
      <AnimatePresence>
        {generationProgress && generationProgress.status === 'running' && (
          <motion.div
            className="vs-generation-banner"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="vs-generation-info">
              <Loader2 size={18} className="spin" />
              <span>
                Generating: <strong>{generationProgress.currentVoiceSet}</strong> / {generationProgress.currentCategory} (var {generationProgress.currentVariation})
              </span>
            </div>
            <div className="vs-generation-progress">
              <div className="vs-generation-bar">
                <div className="vs-generation-fill" style={{ width: `${generationProgress.progress || 0}%` }} />
              </div>
              <span className="vs-generation-percent">{generationProgress.progress || 0}%</span>
              <span className="vs-generation-count">
                {generationProgress.completedFiles}/{generationProgress.totalFiles}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <motion.div className="vs-header" variants={itemVariants}>
        <div className="vs-header-left">
          {view !== 'list' && view !== 'global-categories' && (
            <button className="vs-back-btn" onClick={() => {
              stopAudio()
              if (view === 'files') { setSelectedCategory(null); setView('categories') }
              else { setSelectedVoiceSet(null); setView('list'); setMetadataStatus(null) }
            }}>
              <ArrowLeft size={18} />
            </button>
          )}
          {view === 'global-categories' && (
            <button className="vs-back-btn" onClick={() => setView('list')}>
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="vs-breadcrumb">
            <span className={view === 'list' ? 'active' : 'clickable'} onClick={() => { if (view !== 'list') { setSelectedVoiceSet(null); setSelectedCategory(null); setView('list'); setMetadataStatus(null) } }}>
              <Mic size={16} /> Voice Sets
            </span>
            {view === 'global-categories' && (
              <><ChevronRight size={14} /><span className="active"><Globe size={14} /> Global Categories</span></>
            )}
            {selectedVoiceSet && (
              <><ChevronRight size={14} /><span className={view === 'categories' ? 'active' : 'clickable'} onClick={() => { if (view !== 'categories') { setSelectedCategory(null); setView('categories') } }}><Folder size={14} /> {selectedVoiceSet.name}</span></>
            )}
            {selectedCategory && (
              <><ChevronRight size={14} /><span className="active"><FileAudio size={14} /> {selectedCategory.name}</span></>
            )}
          </div>
        </div>
        <div className="vs-header-right">
          {view === 'list' && (
            <>
              <button className="btn btn-secondary" onClick={() => { setView('global-categories'); loadGlobalCategories() }}>
                <Globe size={16} /> Manage Categories
              </button>
              <button
                className="btn btn-secondary"
                onClick={autoFixAll}
                disabled={autoFixing}
                title="Sync all voice sets with global categories"
              >
                {autoFixing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                {autoFixing ? 'Syncing...' : 'Sync All'}
              </button>
              <button className="btn btn-primary" onClick={() => openVoiceSetModal()}>
                <Plus size={16} /> New Voice Set
              </button>
            </>
          )}
          {view === 'global-categories' && (
            <button className="btn btn-primary" onClick={() => openGlobalCategoryModal()}>
              <Upload size={16} /> Upload Category
            </button>
          )}
          {view === 'categories' && selectedVoiceSet?.elevenlabs_voice_id && (
            <button
              className="btn btn-primary"
              onClick={generateAllCategories}
              disabled={generatingAllCategories}
            >
              {generatingAllCategories ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />}
              {generatingAllCategories ? 'Generating...' : 'Generate All Missing'}
            </button>
          )}
        </div>
      </motion.div>

      {/* STATS */}
      {view === 'list' && !loading && (
        <motion.div className="stat-blocks" variants={itemVariants}>
          <div className="stat-block blue">
            <div className="stat-block-icon"><Mic size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{stats.voiceSetCount}</span>
              <span className="stat-block-label">Voice Sets</span>
            </div>
          </div>
          <div className="stat-block purple">
            <div className="stat-block-icon"><Folder size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{stats.totalCategories}</span>
              <span className="stat-block-label">Categories</span>
            </div>
          </div>
          <div className="stat-block green">
            <div className="stat-block-icon"><FileAudio size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{stats.totalFiles}</span>
              <span className="stat-block-label">Audio Files</span>
            </div>
          </div>
          <div className="stat-block orange">
            <div className="stat-block-icon"><Zap size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{stats.withElevenLabs}</span>
              <span className="stat-block-label">ElevenLabs</span>
            </div>
          </div>
        </motion.div>
      )}

      {view === 'global-categories' && !loading && (
        <motion.div className="vs-stats-bar" variants={itemVariants}>
          <div className="vs-stat">
            <div className="vs-stat-top"><Layers size={18} /><span className="vs-stat-title">Global Categories</span></div>
            <div className="vs-stat-value">{globalCategories.length}</div>
            <div className="vs-stat-label">categories</div>
          </div>
          <div className="vs-stat">
            <div className="vs-stat-top"><Mic size={18} /><span className="vs-stat-title">Voice Sets</span></div>
            <div className="vs-stat-value">{stats.withElevenLabs}</div>
            <div className="vs-stat-label">to generate</div>
          </div>
        </motion.div>
      )}

      {view === 'categories' && !loading && (
        <motion.div className="vs-stats-bar" variants={itemVariants}>
          <div className="vs-stat">
            <div className="vs-stat-top"><Folder size={18} /><span className="vs-stat-title">Categories</span></div>
            <div className="vs-stat-value">{categoryStats.categoryCount}</div>
            <div className="vs-stat-label">total</div>
          </div>
          <div className="vs-stat">
            <div className="vs-stat-top"><FileAudio size={18} /><span className="vs-stat-title">Audio Files</span></div>
            <div className="vs-stat-value">{categoryStats.totalFiles}</div>
            <div className="vs-stat-label">total files</div>
          </div>
          {selectedVoiceSet?.elevenlabs_voice_id && (
            <div className="vs-stat vs-stat-highlight">
              <div className="vs-stat-top"><Zap size={18} /><span className="vs-stat-title">ElevenLabs</span></div>
              <div className="vs-stat-value">✓</div>
              <div className="vs-stat-label">enabled</div>
            </div>
          )}
        </motion.div>
      )}

      {/* METADATA PANEL */}
      {view === 'categories' && !loading && metadataStatus && (
        <motion.div className="vs-metadata-panel" variants={itemVariants}>
          <div className="vs-metadata-header">
            <div className="vs-metadata-title"><MessageSquare size={18} /><span>Phrase Boundaries</span><span className="vs-metadata-subtitle">for natural audio interruption</span></div>
            <div className="vs-metadata-actions">
              {metadataStatus.failed > 0 && !metadataGenerating && <button className="btn btn-secondary btn-sm" onClick={retryFailedMetadata}><RefreshCw size={14} /> Retry Failed ({metadataStatus.failed})</button>}
              <button className="btn btn-primary btn-sm" onClick={() => generateMetadata(false)} disabled={metadataGenerating || (metadataStatus.none == 0 && metadataStatus.failed == 0 && metadataStatus.pending == 0 && metadataStatus.processing == 0)}>
                {metadataGenerating ? <><Loader2 size={14} className="spin" /> Processing...</> : <><Zap size={14} /> Generate Metadata</>}
              </button>
            </div>
          </div>
          <div className="vs-metadata-stats">
            <div className="vs-meta-stat"><CheckCircle2 size={14} className="success" /><span>{metadataStatus.completed || 0}</span><label>Completed</label></div>
            <div className="vs-meta-stat"><Clock size={14} className="pending" /><span>{(metadataStatus.pending || 0) + (metadataStatus.processing || 0)}</span><label>Processing</label></div>
            <div className="vs-meta-stat"><XCircle size={14} className="failed" /><span>{metadataStatus.failed || 0}</span><label>Failed</label></div>
            <div className="vs-meta-stat"><Activity size={14} /><span>{metadataStatus.none || 0}</span><label>Pending</label></div>
          </div>
        </motion.div>
      )}

      {/* LOADING */}
      {loading && (
        <motion.div className="vs-loading" variants={itemVariants}>
          <Loader2 size={32} className="spin" />
          <span>Loading...</span>
        </motion.div>
      )}

      {/* VOICE SETS LIST */}
      {view === 'list' && !loading && (
        <motion.div className="vs-content" variants={itemVariants}>
          <div className="vs-toolbar">
            <div className="vs-search">
              <Search size={16} />
              <input type="text" placeholder="Search voice sets..." value={voiceSetSearch} onChange={(e) => setVoiceSetSearch(e.target.value)} />
            </div>
            {/* Gender filter pills */}
            <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
              {(() => {
                const maleCount = voiceSets.filter(vs => (vs.gender || '').toLowerCase() === 'male').length
                const femaleCount = voiceSets.filter(vs => (vs.gender || '').toLowerCase() === 'female').length
                const pill = (key, label, count) => (
                  <button
                    key={key}
                    onClick={() => setGenderFilter(key)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid ' + (genderFilter === key ? 'var(--primary)' : 'var(--border-color)'),
                      background: genderFilter === key ? 'var(--primary)' : 'transparent',
                      color: genderFilter === key ? '#fff' : 'var(--text-muted)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >{label} {count != null && `(${count})`}</button>
                )
                return [
                  pill('all', 'All', voiceSets.length),
                  pill('male', 'Male', maleCount),
                  pill('female', 'Female', femaleCount)
                ]
              })()}
            </div>
            <div className="vs-sort">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Name</option>
                <option value="categories">Categories</option>
                <option value="files">Files</option>
                <option value="date">Date</option>
              </select>
              <button className="btn btn-ghost btn-sm" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                <ArrowUpDown size={14} />
              </button>
            </div>
          </div>
          {filteredVoiceSets.length === 0 ? (
            <div className="vs-empty">
              <Mic size={48} />
              <h3>No voice sets found</h3>
              <p>Create your first voice set to get started</p>
            </div>
          ) : (
            <div className="vs-grid">
              {paginatedVoiceSets.map((vs, i) => {
                const vsSyncStatus = syncStatus?.voice_sets?.find(s => s.id === vs.id)
                const isMismatched = vsSyncStatus && !vsSyncStatus.is_synced

                return (
                  <motion.div key={vs.id} className={`vs-card ${vs.elevenlabs_voice_id ? 'has-elevenlabs' : ''} ${isMismatched ? 'has-warning' : ''}`} onClick={() => selectVoiceSet(vs)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <div className="vs-card-header">
                      <div className="vs-card-icon"><Mic size={24} /></div>
                      <div className="vs-card-actions">
                        {isMismatched && (
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(e) => { e.stopPropagation(); autoFixVoiceSet(vs.id) }}
                            title={`Missing ${vsSyncStatus.missing_count} categories. Click to auto-fix.`}
                            style={{ color: '#f59e0b' }}
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                        <button className="btn btn-ghost btn-xs" title={vs.minimax_voice_id ? 'Replace cloned voice' : 'Add voice (clone)'} onClick={(e) => { e.stopPropagation(); openMM(vs, 'clone') }} style={vs.minimax_voice_id ? { color: '#22c55e' } : undefined}><Mic size={14} /></button>
                        <button className="btn btn-ghost btn-xs" title={vs.minimax_voice_id ? 'Generate from script' : 'Add a voice first to enable'} disabled={!vs.minimax_voice_id} onClick={(e) => { e.stopPropagation(); openMM(vs, 'generate') }}><Wand2 size={14} /></button>
                        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); openVoiceSetModal(vs) }}><Edit size={14} /></button>
                        <button className="btn btn-ghost btn-xs danger" onClick={(e) => confirmDeleteVoiceSet(vs, e)}><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <h3 className="vs-card-title">{vs.name}</h3>
                    {vs.description && <p className="vs-card-desc">{vs.description}</p>}
                    <div className="vs-card-stats">
                      <span><Folder size={12} /> {vs.category_count || 0} categories</span>
                      <span><FileAudio size={12} /> {vs.file_count || 0} files</span>
                      {vs.minimax_voice_id
                        ? <span style={{ color: '#22c55e' }}><Mic size={12} /> voice ready</span>
                        : <span style={{ color: 'var(--text-muted)' }}><Mic size={12} /> no voice</span>}
                    </div>
                    {isMismatched && (
                      <div className="vs-card-warning" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.75rem',
                        color: '#f59e0b',
                        marginTop: '8px'
                      }}>
                        <AlertTriangle size={12} />
                        <span>Missing {vsSyncStatus.missing_count} categories</span>
                      </div>
                    )}
                    <div className="vs-card-date">{formatDate(vs.created_at)}</div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* GLOBAL CATEGORIES VIEW */}
      {view === 'global-categories' && !loading && (
        <motion.div className="vs-content vs-global-categories" variants={itemVariants}>
          <div className="vs-info-box">
            <AlertCircle size={16} />
            <span>Global categories are shared across all voice sets. Creating a new category will auto-generate audio for all voice sets with ElevenLabs Voice IDs.</span>
          </div>

          {/* Usage filter + bulk selection bar */}
          {globalCategories.length > 0 && (() => {
            const usedCount = globalCategories.filter(c => c.last_used_avatar_id).length
            const unusedCount = globalCategories.length - usedCount
            const search = globalCategorySearch.trim().toLowerCase()
            const visibleCats = globalCategories.filter(cat => {
              if (categoryUsageFilter === 'used' && !cat.last_used_avatar_id) return false
              if (categoryUsageFilter === 'unused' && cat.last_used_avatar_id) return false
              if (search && !(cat.name || '').toLowerCase().includes(search)) return false
              return true
            })
            const allVisibleSelected = visibleCats.length > 0 && visibleCats.every(c => selectedCategoryIds.has(c.id))
            const btnStyle = (active) => ({
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid ' + (active ? 'var(--primary)' : 'var(--border-color)'),
              background: active ? 'var(--primary)' : 'transparent',
              color: active ? '#fff' : 'var(--text-muted)',
              fontSize: '0.8rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s'
            })
            return (
              <>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div className="vs-search" style={{ flex: '0 1 280px', minWidth: '200px' }}>
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={globalCategorySearch}
                      onChange={(e) => setGlobalCategorySearch(e.target.value)}
                    />
                    {globalCategorySearch && (
                      <button
                        type="button"
                        onClick={() => setGlobalCategorySearch('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 4px', display: 'flex', alignItems: 'center' }}
                        title="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginRight: '4px' }}>Filter:</span>
                  <button style={btnStyle(categoryUsageFilter === 'all')} onClick={() => setCategoryUsageFilter('all')}>
                    All ({globalCategories.length})
                  </button>
                  <button style={btnStyle(categoryUsageFilter === 'used')} onClick={() => setCategoryUsageFilter('used')}>
                    Used ({usedCount})
                  </button>
                  <button style={btnStyle(categoryUsageFilter === 'unused')} onClick={() => setCategoryUsageFilter('unused')}>
                    Unused ({unusedCount})
                  </button>
                  <button
                    style={{ ...btnStyle(false), marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => allVisibleSelected ? clearCategorySelection() : selectAllVisibleCategories(visibleCats)}
                    title={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
                  >
                    <input type="checkbox" checked={allVisibleSelected} readOnly style={{ cursor: 'pointer' }} />
                    {allVisibleSelected ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                {selectedCategoryIds.size > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    marginBottom: '16px',
                    background: 'rgba(220, 38, 38, 0.08)',
                    border: '1px solid rgba(220, 38, 38, 0.3)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      <strong>{selectedCategoryIds.size}</strong> categor{selectedCategoryIds.size === 1 ? 'y' : 'ies'} selected
                    </span>
                    <button className="btn btn-danger btn-sm" onClick={bulkDeleteGlobalCategories} disabled={bulkDeleting}>
                      {bulkDeleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                      Delete selected
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={clearCategorySelection}>Clear</button>
                  </div>
                )}
              </>
            )
          })()}

          {globalCategories.length === 0 ? (
            <div className="vs-empty">
              <Layers size={48} />
              <h3>No global categories</h3>
              <p>Create your first category to start generating audio</p>
            </div>
          ) : (
            <div className="vs-categories-grid">
              {globalCategories
                .filter(cat => {
                  if (categoryUsageFilter === 'used' && !cat.last_used_avatar_id) return false
                  if (categoryUsageFilter === 'unused' && cat.last_used_avatar_id) return false
                  const s = globalCategorySearch.trim().toLowerCase()
                  if (s && !(cat.name || '').toLowerCase().includes(s)) return false
                  return true
                })
                .map((cat, i) => (
                <motion.div
                  key={cat.id}
                  className="vs-global-category-card"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  style={selectedCategoryIds.has(cat.id) ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.15)' } : {}}
                >
                  <div className="vs-category-header">
                    <div className="vs-category-top">
                      <input
                        type="checkbox"
                        checked={selectedCategoryIds.has(cat.id)}
                        onChange={() => toggleCategorySelection(cat.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer', width: '15px', height: '15px', marginRight: '2px' }}
                        title="Select"
                      />
                      <Layers size={18} className="vs-category-icon" />
                      <span className="vs-category-name" title={cat.name}>{cat.name}</span>
                    </div>
                    <div className="vs-category-bottom">
                      <span className="vs-category-count">{cat.variation_count || 0} variations</span>
                      <div className="vs-category-actions">
                        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); openGlobalCategoryModal(cat) }}><Edit size={12} /></button>
                        <button className="btn btn-ghost btn-xs danger" onClick={(e) => confirmDeleteGlobalCategory(cat, e)}><Trash2 size={12} /></button>
                      </div>
                    </div>
                  </div>
                  {cat.variations && cat.variations.length > 0 && (
                    <div className="vs-variations-preview">
                      {cat.variations.slice(0, 2).map((v, vi) => (
                        <div key={vi} className="vs-variation-preview">
                          <span className="vs-var-num">#{v.variation_number}</span>
                          <span className="vs-var-text">{v.text_content.substring(0, 50)}{v.text_content.length > 50 ? '...' : ''}</span>
                        </div>
                      ))}
                      {cat.variations.length > 2 && (
                        <div className="vs-more-variations">+{cat.variations.length - 2} more</div>
                      )}
                    </div>
                  )}
                  {/* Last Modified Info */}
                  <div className="vs-last-modified" style={{
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(139, 92, 246, 0.1)',
                    fontSize: '0.7rem',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Clock size={11} />
                    {cat.last_modified_at ? (
                      <span title={cat.last_modified_in ? `In ${cat.last_modified_in}` : ''}>
                        {cat.last_action === 'created' ? 'Created' : 'Modified'}
                        {cat.last_modified_in && <span style={{ color: 'var(--primary)', marginLeft: '3px' }}>({cat.last_modified_in.replace(' VoiceSet', '')})</span>}
                        {' by '}<strong>{cat.last_modified_by || 'Unknown'}</strong>
                        {' · '}
                        {new Date(cat.last_modified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    ) : (
                      <span>Created {new Date(cat.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    )}
                  </div>
                  {/* Last Used In Avatar */}
                  <div className="vs-last-used" style={{
                    marginTop: '4px',
                    fontSize: '0.7rem',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Layers size={11} />
                    {cat.last_used_at && cat.last_used_avatar_name ? (
                      <span title={`Last used in avatar: ${cat.last_used_avatar_name}`}>
                        Last used in <strong style={{ color: 'var(--primary)' }}>{cat.last_used_avatar_name}</strong>
                        {' · '}
                        {new Date(cat.last_used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    ) : (
                      <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Not used in any avatar</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* CATEGORIES VIEW (for single voice set) */}
      {view === 'categories' && !loading && (
        <motion.div className="vs-content vs-categories-view" variants={itemVariants}>
          {/* Sync Status Banner for this voice set */}
          {(() => {
            const vsSyncInfo = syncStatus?.voice_sets?.find(s => s.id === selectedVoiceSet?.id)
            if (vsSyncInfo && !vsSyncInfo.is_synced) {
              return (
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b10, #f9731610)',
                  border: '1px solid #f59e0b30',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
                        <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                          Missing {vsSyncInfo.missing_count} categories
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {vsSyncInfo.missing_categories.slice(0, 5).map((name, i) => (
                          <span key={i} style={{
                            display: 'inline-block',
                            background: 'var(--bg-tertiary)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginRight: '4px',
                            marginBottom: '4px'
                          }}>
                            {name}
                          </span>
                        ))}
                        {vsSyncInfo.missing_categories.length > 5 && (
                          <span style={{ color: 'var(--text-muted)' }}>
                            +{vsSyncInfo.missing_categories.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => autoFixVoiceSet(selectedVoiceSet.id)}
                      disabled={autoFixing}
                      style={{ background: '#f59e0b', color: 'white', flexShrink: 0 }}
                    >
                      {autoFixing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                      Auto-Fix
                    </button>
                  </div>
                </div>
              )
            }
            return null
          })()}

          <div className="vs-toolbar">
            <div className="vs-search">
              <Search size={16} />
              <input type="text" placeholder="Search categories..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => folderInputRef.current?.click()}
              title="Upload a folder of WAV files. The folder structure should be category-name/file.wav — each subfolder becomes a category."
              style={{ marginLeft: 'auto' }}
            >
              <FolderPlus size={16} /> Upload Folder
            </button>
            <input
              ref={folderInputRef}
              type="file"
              multiple
              webkitdirectory=""
              directory=""
              style={{ display: 'none' }}
              onChange={handleFolderUpload}
            />
          </div>
          {filteredCategories.length === 0 ? (
            <div className="vs-empty"><Folder size={48} /><h3>No categories</h3><p>Add categories or use bulk upload</p></div>
          ) : (
            <div className="vs-categories-grid">
              {filteredCategories.map((cat, i) => {
                // Check if created within last 7 days
                const createdDate = cat.created_at ? new Date(cat.created_at) : null
                const isRecent = createdDate && (Date.now() - createdDate.getTime()) < 7 * 24 * 60 * 60 * 1000

                return (
                  <motion.div
                    key={cat.id}
                    className={`vs-category-card ${isRecent ? 'is-recent' : ''}`}
                    onClick={() => selectCategory(cat)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    style={isRecent ? { borderColor: '#22c55e50', background: 'linear-gradient(135deg, var(--bg-secondary), rgba(34, 197, 94, 0.05))' } : {}}
                  >
                    <div className="vs-category-header">
                      <div className="vs-category-top">
                        <Folder size={18} className="vs-category-icon" />
                        <span className="vs-category-name">
                          {cat.name}
                          {isRecent && (
                            <span style={{
                              fontSize: '0.6rem',
                              background: '#22c55e',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              marginLeft: '8px',
                              verticalAlign: 'middle'
                            }}>
                              NEW
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="vs-category-bottom">
                        <span className="vs-category-count">{cat.file_count || 0} files</span>
                        <div className="vs-category-actions">
                          <button className="btn btn-ghost btn-xs" title="Play" disabled={!cat.file_count} onClick={(e) => playCategory(cat, e)}>
                            {playingCategoryId === cat.id ? <Pause size={12} /> : <Play size={12} />}
                          </button>
                          <button className="btn btn-ghost btn-xs" onClick={(e) => openCategoryModal(cat, e)}><Edit size={12} /></button>
                          <button className="btn btn-ghost btn-xs danger" onClick={(e) => confirmDeleteCategory(cat, e)}><Trash2 size={12} /></button>
                        </div>
                      </div>
                    </div>
                    {cat.description && <p className="vs-category-desc">{cat.description}</p>}
                  </motion.div>
                )
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* FILES VIEW WITH METADATA */}
      {view === 'files' && !loading && (
        <motion.div className="vs-content vs-files-view" variants={itemVariants}>
          <AnimatePresence>{audioError && <motion.div className="vs-audio-error" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}><AlertCircle size={16} /><span>{audioError}</span><button onClick={() => setAudioError(null)}><X size={14} /></button></motion.div>}</AnimatePresence>

          {/* Upload WAVs to this category */}
          {selectedCategory && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px', gap: '8px' }}>
              <button
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                title={`Upload one or more WAV files into the "${selectedCategory.name}" category.`}
              >
                <Upload size={16} /> {saving ? 'Uploading…' : `Upload WAVs to ${selectedCategory.name}`}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".wav,audio/wav"
                style={{ display: 'none' }}
                onChange={handleFilesUpload}
              />
            </div>
          )}

          {/* Variations Panel - Show global category variations with editing */}
          {categoryVariations?.has_global && selectedVoiceSet?.elevenlabs_voice_id && (
            <div className="vs-variations-panel" style={{
              marginBottom: '16px',
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Wand2 size={18} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontWeight: '600' }}>Text Variations</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {categoryVariations.generated_count}/{categoryVariations.total_variations} generated
                  </span>
                  {categoryVariations.variations.some(v => v.is_customized) && (
                    <span style={{
                      fontSize: '0.75rem',
                      background: '#8b5cf620',
                      color: '#8b5cf6',
                      padding: '2px 8px',
                      borderRadius: '10px'
                    }}>
                      {categoryVariations.variations.filter(v => v.is_customized).length} customized
                    </span>
                  )}
                </div>
                {categoryVariations.variations.some(v => !v.has_file) && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={generateAllMissing}
                    disabled={generatingAll}
                  >
                    {generatingAll ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
                    Generate All Missing ({categoryVariations.variations.filter(v => !v.has_file).length})
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categoryVariations.variations.map((v, i) => {
                  const isEditing = editingVariation === v.variation_number
                  const isExpanded = expandedVariation === v.variation_number
                  const isPlaying = playingVariation === v.variation_number

                  return (
                    <div key={i} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0',
                      background: v.is_customized
                        ? 'rgba(139, 92, 246, 0.1)'
                        : v.has_file
                          ? 'rgba(34, 197, 94, 0.1)'
                          : 'rgba(251, 191, 36, 0.1)',
                      borderRadius: '8px',
                      border: `1px solid ${v.is_customized ? 'rgba(139, 92, 246, 0.3)' : v.has_file ? 'rgba(34, 197, 94, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
                      overflow: 'hidden'
                    }}>
                      {/* Main Row */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        cursor: v.has_file ? 'pointer' : 'default'
                      }}
                        onClick={() => v.has_file && !isEditing && toggleExpandedVariation(v.variation_number)}
                      >
                        {/* Play Button */}
                        {v.has_file ? (
                          <button
                            className={`vs-play-btn ${isPlaying ? 'playing' : ''}`}
                            onClick={(e) => { e.stopPropagation(); playVariationAudio(v) }}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isPlaying ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                              border: 'none',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                          >
                            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                        ) : (
                          <span style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--bg-tertiary)',
                            borderRadius: '50%',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {v.variation_number}
                          </span>
                        )}

                        {/* Text Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '0.85rem',
                            color: 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {v.has_file && <span style={{ color: 'var(--text-muted)', marginRight: '4px' }}>#{v.variation_number}</span>}
                              "{v.effective_text || v.global_text}"
                            </span>
                            {v.is_customized && (
                              <span style={{
                                fontSize: '0.65rem',
                                background: '#8b5cf6',
                                color: 'white',
                                padding: '1px 5px',
                                borderRadius: '6px',
                                flexShrink: 0
                              }}>
                                custom
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {v.has_file && v.file?.duration_ms && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                {formatDuration(v.file.duration_ms)}
                              </span>
                            )}
                            {v.is_customized && v.modified_by && (
                              <span style={{
                                fontSize: '0.65rem',
                                color: 'var(--text-muted)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px'
                              }} title={`Modified by ${v.modified_by} on ${new Date(v.modified_at).toLocaleString()}`}>
                                <Clock size={10} />
                                {v.modified_by.split(' ')[0]} • {new Date(v.modified_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          {v.has_file ? (
                            <>
                              <button
                                className="btn btn-ghost btn-xs"
                                onClick={() => regenerateVariationAudio(v)}
                                disabled={generatingVariation === v.variation_number}
                                title="Regenerate audio"
                              >
                                {generatingVariation === v.variation_number ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                              </button>
                              <button
                                className="btn btn-ghost btn-xs"
                                onClick={() => toggleExpandedVariation(v.variation_number)}
                                title={isExpanded ? "Collapse" : "Expand to edit"}
                              >
                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-secondary btn-xs"
                              onClick={() => generateVariation(v.variation_number)}
                              disabled={generatingVariation === v.variation_number}
                              title="Generate audio"
                            >
                              {generatingVariation === v.variation_number ? <Loader2 size={12} className="spin" /> : <Zap size={12} />}
                              Generate
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Section - Waveform & Edit Text */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ overflow: 'hidden' }}
                          >
                            {/* Full Waveform Player */}
                            {v.has_file && (
                              <div style={{ padding: '12px', borderTop: '1px solid var(--border-color)' }}>
                                <AudioWaveform
                                  audioUrl={`${API_BASE_URL}${v.file.download_url}`}
                                  isPlaying={isPlaying}
                                  currentTime={isPlaying ? variationCurrentTime : 0}
                                  duration={v.file.duration_ms / 1000}
                                  height={64}
                                  onSeek={seekVariationAudio}
                                  onPlay={() => playVariationAudio(v)}
                                />
                              </div>
                            )}

                            <div style={{
                              padding: '12px',
                              borderTop: '1px solid var(--border-color)',
                              background: 'var(--bg-tertiary)'
                            }}>
                              <div style={{ marginBottom: '8px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                Edit text for this variation:
                              </div>
                              {isEditing ? (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    className="input"
                                    value={editedText}
                                    onChange={(e) => setEditedText(e.target.value)}
                                    style={{ flex: 1, fontSize: '0.85rem' }}
                                    autoFocus
                                  />
                                  <button className="btn btn-ghost btn-xs" onClick={cancelEditingVariation}><X size={14} /></button>
                                  <button
                                    className="btn btn-primary btn-xs"
                                    onClick={() => saveTextOverride(v.variation_number, editedText)}
                                    disabled={savingOverride || !editedText.trim()}
                                  >
                                    {savingOverride ? <Loader2 size={12} className="spin" /> : <Save size={12} />} Save
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-xs"
                                    onClick={async () => {
                                      await saveTextOverride(v.variation_number, editedText)
                                      regenerateVariationAudio({ ...v, file: v.file })
                                    }}
                                    disabled={savingOverride || !editedText.trim() || generatingVariation === v.variation_number}
                                  >
                                    {generatingVariation === v.variation_number ? <Loader2 size={12} className="spin" /> : <Zap size={12} />} Save & Regenerate
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <div
                                    style={{
                                      flex: 1,
                                      padding: '8px 12px',
                                      background: 'var(--bg-primary)',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      border: '1px solid var(--border-color)'
                                    }}
                                    onClick={() => startEditingVariation(v)}
                                  >
                                    <span style={{ fontSize: '0.85rem' }}>"{v.effective_text || v.global_text}"</span>
                                  </div>
                                  <button className="btn btn-ghost btn-xs" onClick={() => startEditingVariation(v)} title="Edit"><Edit size={14} /></button>
                                  {v.is_customized && (
                                    <button
                                      className="btn btn-ghost btn-xs"
                                      onClick={() => revertToGlobal(v.variation_number)}
                                      style={{ color: '#f59e0b' }}
                                      title="Revert to global text"
                                    >
                                      <RotateCcw size={14} />
                                    </button>
                                  )}
                                </div>
                              )}
                              {v.is_customized && !isEditing && (
                                <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                  Global text: "{v.global_text}"
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {filteredFiles.length === 0 && !categoryVariations?.has_global ? <div className="vs-empty"><Music size={48} /><h3>No audio files</h3><p>Upload WAV files to this category</p></div> : filteredFiles.length === 0 ? null : (
            <div className="vs-files-list">
              {filteredFiles.map((file, index) => {
                const isPlaying = playingFile === file.id
                const isPaused = isPlaying && audioRef.current.paused
                const isLoading = isPlaying && audioLoading
                const isExpanded = expandedFile === file.id
                const phraseCount = getPhraseCount(file)
                const firstPhrase = getFirstPhrase(file)
                const hasMetadata = file.metadata_status === 'completed' && phraseCount > 0

                return (
                  <motion.div key={file.id} className={`vs-file-card ${isPlaying ? 'playing' : ''} ${isExpanded ? 'expanded' : ''}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}>
                    <div className="vs-file-main">
                      <button className={`vs-play-btn ${isPlaying ? (isPaused ? 'paused' : 'playing') : ''}`} onClick={() => playAudio(file)} disabled={isLoading}>
                        {isLoading ? <Loader2 size={16} className="spin" /> : isPlaying && !isPaused ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <div className="vs-file-info">
                        <div className="vs-file-name-row">
                          <span className="vs-file-name">{file.display_name || file.filename}</span>
                          {file.duration_ms && <span className="vs-file-duration">{formatDuration(file.duration_ms)}</span>}
                          {hasMetadata && <span className="vs-phrase-badge" title={`${phraseCount} phrase${phraseCount !== 1 ? 's' : ''}`}><MessageSquare size={10} /> {phraseCount}</span>}
                        </div>
                        {firstPhrase && <div className="vs-file-transcription">"{firstPhrase}"{phraseCount > 1 && <span className="vs-more-phrases">+{phraseCount - 1} more</span>}</div>}
                      </div>
                      {isPlaying && <div className="vs-audio-progress" onClick={(e) => seekAudio(e, file)}><div className="vs-audio-progress-bar" style={{ width: `${(audioProgress / audioDuration) * 100 || 0}%` }} /><span className="vs-audio-time">{formatTime(audioProgress)} / {formatTime(audioDuration)}</span></div>}
                      <div className="vs-file-actions">
                        {hasMetadata && <button className={`btn btn-ghost btn-xs ${isExpanded ? 'active' : ''}`} onClick={() => toggleFileExpand(file.id)} title="View phrases">{isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>}
                        {isPlaying && <button className="btn btn-ghost btn-xs" onClick={stopAudio} title="Stop"><Square size={14} /></button>}
                        {selectedVoiceSet?.elevenlabs_voice_id && (
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(e) => regenerateFile(file, e)}
                            title="Regenerate audio"
                            disabled={regeneratingFile === file.id}
                          >
                            {regeneratingFile === file.id ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                          </button>
                        )}
                        {selectedVoiceSet?.minimax_voice_id && (
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={(e) => regenerateFileMM(file, e)}
                            title={file.transcription?.trim() ? 'Regenerate this clip (MiniMax)' : 'No script text to regenerate'}
                            disabled={regeneratingFile === file.id || !file.transcription?.trim()}
                          >
                            {regeneratingFile === file.id ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                          </button>
                        )}
                        <button className="btn btn-ghost btn-xs" onClick={(e) => openEditScript(file, e)} title="Edit script"><Edit size={14} /></button>
                        <a href={`${API_BASE_URL}/voice-sets/files/${encodeURIComponent(file.filename)}`} download className="btn btn-ghost btn-xs" title="Download" onClick={e => e.stopPropagation()}><Download size={14} /></a>
                        <button className="btn btn-ghost btn-xs danger" onClick={(e) => deleteFile(file, e)} title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <AnimatePresence>
                      {isExpanded && hasMetadata && (
                        <motion.div className="vs-phrase-details" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <div className="vs-phrase-timeline">
                            {getPhrases(file).map((phrase, i) => {
                              const duration = file.boundaries?.duration || (file.duration_ms / 1000)
                              const left = (phrase.start / duration) * 100
                              const width = ((phrase.end - phrase.start) / duration) * 100
                              return <div key={i} className="vs-phrase-segment" style={{ left: `${left}%`, width: `${Math.max(width, 2)}%` }} title={phrase.text} />
                            })}
                          </div>
                          <div className="vs-phrase-list">
                            {getPhrases(file).map((phrase, i) => <div key={i} className="vs-phrase-item"><div className="vs-phrase-index">{i + 1}</div><div className="vs-phrase-text">"{phrase.text}"</div><div className="vs-phrase-time">{formatTimestamp(phrase.start)} → {formatTimestamp(phrase.end)}</div></div>)}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* RECENT CHANGES SECTION */}
      {view === 'list' && (
        <motion.div
          className="vs-recent-changes"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            marginTop: '32px',
            padding: '20px',
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
              <Clock size={18} /> Recent Changes
            </h3>
            <button
              className="btn btn-ghost btn-sm"
              onClick={fetchRecentChanges}
              disabled={changesLoading}
              style={{ padding: '4px 8px' }}
            >
              <RefreshCw size={14} className={changesLoading ? 'spin' : ''} />
            </button>
          </div>

          {changesLoading && recentChanges.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
              <Loader2 size={24} className="spin" />
            </div>
          ) : recentChanges.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
              No recent changes recorded
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {recentChanges.map((change, idx) => (
                <div
                  key={change.id || idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '10px 12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.85rem'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: change.action === 'created' ? 'rgba(34, 197, 94, 0.15)' :
                               change.action === 'deleted' ? 'rgba(239, 68, 68, 0.15)' :
                               'rgba(59, 130, 246, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {change.action === 'created' && <Plus size={14} style={{ color: '#22c55e' }} />}
                    {change.action === 'deleted' && <Trash2 size={14} style={{ color: '#ef4444' }} />}
                    {change.action === 'updated' && <Edit size={14} style={{ color: '#3b82f6' }} />}
                    {!['created', 'deleted', 'updated'].includes(change.action) && <Activity size={14} style={{ color: '#6b7280' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        background: change.entity_type === 'voice_set' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(236, 72, 153, 0.15)',
                        color: change.entity_type === 'voice_set' ? '#8b5cf6' : '#ec4899'
                      }}>
                        {change.entity_type === 'voice_set' ? 'Voice Set' : 'Category'}
                      </span>
                      <span style={{ fontWeight: '500' }}>{change.entity_name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>was {change.action}</span>
                    </div>
                    {change.field_changed && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        Changed: {change.field_changed}
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{change.user_name || 'Unknown user'}</span>
                      <span>•</span>
                      <span>{new Date(change.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* MODALS */}
      <AnimatePresence>
        {showModal && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !bulkUploading && !saving && !cloning && !scriptGenerating && setShowModal(false)}>
            <motion.div className="modal" style={{ maxWidth: modalType === 'bulk' ? '700px' : modalType === 'global-category' ? '600px' : modalType === 'script-studio' ? '640px' : modalType === 'clone-voice' ? '560px' : '500px' }} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={e => e.stopPropagation()}>

              {/* VOICE SET MODAL (Enhanced with ElevenLabs) */}
              {modalType === 'voiceset' && (
                <>
                  <div className="modal-header"><h2>{modalMode === 'add' ? 'Create' : 'Edit'} Voice Set</h2><button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button></div>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="input-group">
                      <label className="input-label">Name *</label>
                      <input type="text" className="input" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Mark Voice" autoFocus />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Description</label>
                      <input type="text" className="input" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" />
                    </div>
                    {modalMode === 'add' ? (
                      <>
                        <div className="input-group">
                          <label className="input-label">
                            <Mic size={14} /> Voice sample
                            <span className="input-hint">Upload a clean 10–60s clip — we clone it with MiniMax</span>
                          </label>
                          <input type="file" className="input" accept="audio/*"
                            onChange={(e) => setVsSampleFile(e.target.files?.[0] || null)} />
                          {vsSampleFile && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>{vsSampleFile.name}</div>}
                        </div>

                        <div className="input-group">
                          <label className="input-label">Gender</label>
                          <div className="gender-select">
                            <label className={`gender-option ${formData.gender === 'female' ? 'selected' : ''}`}>
                              <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} />
                              Female
                            </label>
                            <label className={`gender-option ${formData.gender === 'male' ? 'selected' : ''}`}>
                              <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} />
                              Male
                            </label>
                          </div>
                        </div>

                        <div className="input-group" style={{ border: '1px solid rgba(139,92,246,0.3)', borderRadius: 10, padding: 12 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                            <input type="checkbox" checked={vsFullAvatar} onChange={(e) => setVsFullAvatar(e.target.checked)} />
                            <Sparkles size={15} /> Full avatar sound creation
                          </label>
                          <div style={{ fontSize: 12, opacity: 0.65, margin: '6px 0 0 26px' }}>
                            Pick a campaign — its scripts get generated in this voice and a ready avatar is built automatically.
                          </div>
                          {vsFullAvatar && (
                            <div style={{ marginTop: 10 }}>
                              <select className="input" value={vsCampaignId} onChange={(e) => setVsCampaignId(e.target.value)}>
                                <option value="">— select campaign —</option>
                                {campaignsList.map(c => (
                                  <option key={c.id} value={c.id}>{c.name}{c.company ? ' · ' + c.company : ''} ({c.script_count} scripts)</option>
                                ))}
                              </select>
                              {campaignsList.length === 0 && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>No campaigns yet — create one in Campaign Builder.</div>}
                            </div>
                          )}
                        </div>

                        {vsCreateStatus && (
                          <div className="vs-warning-box"><Loader2 size={16} className="spin" /><span>{vsCreateStatus}</span></div>
                        )}
                      </>
                    ) : (
                      <div className="input-group">
                        <label className="input-label">Gender</label>
                        <div className="gender-select">
                          <label className={`gender-option ${formData.gender === 'female' ? 'selected' : ''}`}>
                            <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} />
                            Female
                          </label>
                          <label className={`gender-option ${formData.gender === 'male' ? 'selected' : ''}`}>
                            <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} />
                            Male
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => !saving && setShowModal(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={saveVoiceSet} disabled={saving || !formData.name?.trim()}>
                      {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />} {modalMode === 'add' ? 'Create' : 'Update'}
                    </button>
                  </div>
                </>
              )}

              {/* CLONE VOICE MODAL */}
              {modalType === 'clone-voice' && (
                <>
                  <div className="modal-header">
                    <h2><Mic size={18} /> Clone a Voice</h2>
                    <button className="modal-close" onClick={() => !cloning && setShowModal(false)}><X size={20} /></button>
                  </div>
                  <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
                      Upload one or more clean audio samples - your own voice, or a voice you have the rights to use.
                      1-3 minutes of clear speech with no background noise works best.
                    </div>

                    <div className="input-group">
                      <label className="input-label">Voice Name *</label>
                      <input type="text" className="input" value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., My Voice" autoFocus />
                    </div>

                    <div className="input-group">
                      <label className="input-label">Description</label>
                      <input type="text" className="input" value={formData.description || ''}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional" />
                    </div>

                    <div className="input-group">
                      <label className="input-label">
                        <Upload size={14} /> Audio Sample(s) *
                      </label>
                      <input type="file" className="input" accept="audio/*" multiple
                        onChange={e => setCloneFiles(Array.from(e.target.files || []))} />
                      {cloneFiles.length > 0 && (
                        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
                          {cloneFiles.length} file(s): {cloneFiles.map(f => f.name).join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="input-group">
                      <label className="input-label">Gender</label>
                      <div className="gender-select">
                        <label className={`gender-option ${formData.gender === 'female' ? 'selected' : ''}`}>
                          <input type="radio" name="clone-gender" value="female" checked={formData.gender === 'female'} onChange={e => setFormData({ ...formData, gender: e.target.value })} />
                          Female
                        </label>
                        <label className={`gender-option ${formData.gender === 'male' ? 'selected' : ''}`}>
                          <input type="radio" name="clone-gender" value="male" checked={formData.gender === 'male'} onChange={e => setFormData({ ...formData, gender: e.target.value })} />
                          Male
                        </label>
                      </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!formData.remove_background_noise}
                        onChange={e => setFormData({ ...formData, remove_background_noise: e.target.checked })} />
                      Remove background noise from samples
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!formData.create_voice_set}
                        onChange={e => setFormData({ ...formData, create_voice_set: e.target.checked })} />
                      Create a Voice Set for this clone (so bots/avatars can use it)
                    </label>

                    <EmotionPanel
                      settings={formData.voice_settings}
                      onChange={(vs) => setFormData({ ...formData, voice_settings: vs })}
                      models={availableModels}
                    />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={cloning}>Cancel</button>
                    <button className="btn btn-primary" onClick={cloneVoiceSubmit}
                      disabled={cloning || !formData.name?.trim() || cloneFiles.length === 0}>
                      {cloning ? <Loader2 size={16} className="spin" /> : <Mic size={16} />}
                      {cloning ? 'Cloning...' : 'Clone Voice'}
                    </button>
                  </div>
                </>
              )}

              {/* SCRIPT STUDIO MODAL */}
              {modalType === 'script-studio' && (() => {
                const voiceSetsWithVoice = voiceSets.filter(v => v.elevenlabs_voice_id)
                const selectedVs = voiceSets.find(v => String(v.id) === String(scriptVoiceSetId))
                return (
                  <>
                    <div className="modal-header">
                      <h2><FileAudio size={18} /> Script Studio</h2>
                      <button className="modal-close" onClick={() => !scriptGenerating && setShowModal(false)}><X size={20} /></button>
                    </div>
                    <div className="modal-body" style={{ maxHeight: '74vh', overflowY: 'auto' }}>
                      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
                        Type any script, pick a voice, tune the emotion, then generate - play it back and download the audio.
                      </div>

                      {voiceSetsWithVoice.length === 0 ? (
                        <div className="vs-warning-box">
                          <AlertTriangle size={16} />
                          <span>No voice sets with an ElevenLabs voice yet. Clone a voice or create a voice set first.</span>
                        </div>
                      ) : (
                        <>
                          <div className="input-group">
                            <label className="input-label">Voice</label>
                            <select className="input" value={scriptVoiceSetId}
                              onChange={e => pickScriptVoiceSet(e.target.value)}>
                              {voiceSetsWithVoice.map(v => (
                                <option key={v.id} value={v.id}>{v.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="input-group">
                            <label className="input-label">
                              Script
                              <span className="input-hint">{scriptText.length} characters</span>
                            </label>
                            <textarea className="input" rows={8}
                              style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                              value={scriptText}
                              onChange={e => setScriptText(e.target.value)}
                              placeholder={'Type or paste your script here...\n\nWith the Eleven v3 model you can add emotion cues like [excited], [whispers] or [laughs].'} />
                            {scriptText.length > 2500 && (
                              <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>
                                Long scripts may hit the model's per-request limit - split into shorter parts if generation fails.
                              </div>
                            )}
                          </div>

                          <EmotionPanel
                            settings={scriptSettings}
                            onChange={setScriptSettings}
                            models={availableModels}
                          />

                          {scriptAudio && (
                            <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid rgba(34,197,94,0.35)', borderRadius: 8, background: 'rgba(34,197,94,0.06)' }}>
                              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CheckCircle2 size={15} /> Generated audio
                              </div>
                              <audio controls src={scriptAudio} style={{ width: '100%' }} />
                              <a className="btn btn-secondary" style={{ width: '100%', marginTop: 8 }}
                                href={scriptAudio}
                                download={`script-${(selectedVs?.name || 'voice').replace(/[^a-zA-Z0-9_-]/g, '')}.mp3`}>
                                <Download size={14} /> Download MP3
                              </a>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={scriptGenerating}>Close</button>
                      <button className="btn btn-primary" onClick={generateScript}
                        disabled={scriptGenerating || !scriptText.trim() || voiceSetsWithVoice.length === 0}>
                        {scriptGenerating ? <Loader2 size={16} className="spin" /> : <Volume2 size={16} />}
                        {scriptGenerating ? 'Generating...' : 'Generate'}
                      </button>
                    </div>
                  </>
                )
              })()}

              {/* GLOBAL CATEGORY MODAL */}
              {modalType === 'global-category' && (
                <>
                  <div className="modal-header"><h2><Upload size={18} /> {modalMode === 'add' ? 'Upload' : 'Edit'} Audio Category</h2><button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button></div>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="input-group">
                      <label className="input-label">Category Name *</label>
                      <input type="text" className="input" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., GreetingsIntro" autoFocus />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Description</label>
                      <input type="text" className="input" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" />
                    </div>

                    {/* Gender Toggle */}
                    <div className="input-group">
                      <label className="vs-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={sameForBoth}
                          onChange={(e) => setSameForBoth(e.target.checked)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>Same text for Male & Female voices</span>
                      </label>
                      <span className="input-hint">
                        {sameForBoth
                          ? 'All voice sets will use the same text'
                          : 'Different text for male/female voice sets (e.g., "Hi, I\'m Sarah" vs "Hi, I\'m Mark")'}
                      </span>
                    </div>

                    {voiceTab !== 'upload' && (
                    <div className="input-group">
                      <label className="input-label">Text Variations * (minimum 3)</label>
                      <div className="variations-list">
                        {variations.map((v, i) => (
                          <div key={i} className="variation-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span className="variation-num">{i + 1}</span>
                              {sameForBoth ? (
                                <textarea
                                  className="input variation-input"
                                  value={v.male || ''}
                                  onChange={(e) => updateVariation(i, 'male', e.target.value)}
                                  placeholder={`Variation ${i + 1} text...`}
                                  rows={2}
                                />
                              ) : (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.7rem', color: '#3b82f6', fontWeight: '600', width: '50px' }}>♂ Male</span>
                                    <textarea
                                      className="input variation-input"
                                      value={v.male || ''}
                                      onChange={(e) => updateVariation(i, 'male', e.target.value)}
                                      placeholder={`Male variation ${i + 1}... (e.g., "Hi, I'm Mark")`}
                                      rows={2}
                                      style={{ flex: 1 }}
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '0.7rem', color: '#ec4899', fontWeight: '600', width: '50px' }}>♀ Female</span>
                                    <textarea
                                      className="input variation-input"
                                      value={v.female || ''}
                                      onChange={(e) => updateVariation(i, 'female', e.target.value)}
                                      placeholder={`Female variation ${i + 1}... (e.g., "Hi, I'm Sarah")`}
                                      rows={2}
                                      style={{ flex: 1 }}
                                    />
                                  </div>
                                </div>
                              )}
                              {i >= 3 && (
                                <button className="btn btn-ghost btn-xs danger" onClick={() => removeVariation(i)}><X size={14} /></button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button className="btn btn-secondary btn-sm" onClick={addVariation}><Plus size={14} /> Add Variation</button>
                    </div>
                    )}

                    {/* TAB 1: Existing voice - preview before generating */}
                    {modalMode === 'add' && voiceTab === 'existing' && (
                      <>
                        <div className="input-group" style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                          <label className="input-label">
                            <Volume2 size={14} style={{ marginRight: '6px' }} />
                            Test Voice (preview before generating)
                          </label>
                          <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                            <select className="input" value={previewVoiceId}
                              onChange={(e) => {
                                setPreviewVoiceId(e.target.value)
                                const voice = availableVoices.find(v => v.id === e.target.value)
                                  || clonedVoices.find(v => v.id === e.target.value)
                                if (voice && !sameForBoth) setPreviewGender(voice.gender)
                              }}
                              style={{ flex: 1 }}>
                              <option value="">-- Select a voice --</option>
                              {clonedVoices.length > 0 && (
                                <optgroup label="★ Cloned Voices">
                                  {clonedVoices.map(voice => (
                                    <option key={voice.id} value={voice.id}>{voice.name}</option>
                                  ))}
                                </optgroup>
                              )}
                              <optgroup label="♀ Female Voices">
                                {availableVoices.filter(v => v.gender === 'female').map(voice => (
                                  <option key={voice.id} value={voice.id}>{voice.name}</option>
                                ))}
                              </optgroup>
                              <optgroup label="♂ Male Voices">
                                {availableVoices.filter(v => v.gender === 'male').map(voice => (
                                  <option key={voice.id} value={voice.id}>{voice.name}</option>
                                ))}
                              </optgroup>
                            </select>
                            {!sameForBoth && (
                              <select className="input" value={previewGender}
                                onChange={(e) => setPreviewGender(e.target.value)}
                                style={{ width: '140px' }}>
                                <option value="male">♂ Male Text</option>
                                <option value="female">♀ Female Text</option>
                              </select>
                            )}
                            <button className="btn btn-secondary" onClick={testVariationPreviews}
                              disabled={previewLoading || !previewVoiceId || variations.filter(v => v.male?.trim()).length < 3}>
                              {previewLoading ? <Loader2 size={16} className="spin" /> : <Play size={16} />}
                              Test
                            </button>
                          </div>
                          <span className="input-hint">
                            {sameForBoth
                              ? 'Select a voice to test how your variations sound'
                              : `Testing ${previewGender} text variations with selected voice`}
                          </span>
                        </div>

                        {previews.length > 0 && (
                          <div className="vs-preview-results" style={{ marginTop: '12px' }}>
                            <label className="input-label" style={{ color: previews.every(p => p.success) ? '#22c55e' : '#f59e0b' }}>
                              <CheckCircle2 size={14} style={{ marginRight: '6px' }} />
                              Preview Results ({previews.filter(p => p.success).length}/{previews.length} successful)
                            </label>
                            <div className="vs-preview-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {previews.map((preview, i) => (
                                <div key={i} className="vs-preview-item"
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                                    background: preview.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    borderRadius: '8px',
                                    border: `1px solid ${preview.success ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                  }}>
                                  {preview.success ? (
                                    <button className={`vs-play-btn ${previewPlaying === preview.variation_number ? 'playing' : ''}`}
                                      onClick={() => playPreview(preview)}
                                      style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                      {previewPlaying === preview.variation_number ? <Pause size={14} /> : <Play size={14} />}
                                    </button>
                                  ) : (
                                    <AlertCircle size={20} style={{ color: '#ef4444' }} />
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                      Variation {preview.variation_number}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {preview.success ? preview.text.substring(0, 60) + (preview.text.length > 60 ? '...' : '') : preview.error}
                                    </div>
                                  </div>
                                  {preview.success && (
                                    <CheckCircle2 size={16} style={{ color: '#22c55e' }} />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* TAB 2: Clone a voice for this category */}
                    {modalMode === 'add' && voiceTab === 'clone' && (
                      <div className="input-group" style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                        <label className="input-label">
                          <Mic size={14} style={{ marginRight: '6px' }} />
                          Clone a Voice for this category
                        </label>
                        <div style={{ border: '1px solid rgba(139,92,246,0.35)', borderRadius: 8, padding: '12px 14px', background: 'rgba(139,92,246,0.05)' }}>
                          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                            Upload 1-3 minutes of clean speech - your own voice, or one you have the rights to use. It is saved as a voice set, and this category is generated in it along with all your other voice sets.
                          </div>
                          <div className="input-group">
                            <label className="input-label">Voice Name *</label>
                            <input type="text" className="input" value={catClone.name}
                              onChange={e => setCatClone(c => ({ ...c, name: e.target.value }))}
                              placeholder="e.g., My Voice" />
                          </div>
                          <div className="input-group">
                            <label className="input-label"><Upload size={13} /> Audio Sample(s) *</label>
                            <input type="file" className="input" accept="audio/*" multiple
                              onChange={e => setCatClone(c => ({ ...c, files: Array.from(e.target.files || []) }))} />
                            {catClone.files.length > 0 && (
                              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
                                {catClone.files.length} file(s): {catClone.files.map(f => f.name).join(', ')}
                              </div>
                            )}
                          </div>
                          <div className="input-group">
                            <label className="input-label">Gender</label>
                            <div className="gender-select">
                              <label className={`gender-option ${catClone.gender === 'female' ? 'selected' : ''}`}>
                                <input type="radio" name="cat-clone-gender" value="female" checked={catClone.gender === 'female'} onChange={e => setCatClone(c => ({ ...c, gender: e.target.value }))} />
                                Female
                              </label>
                              <label className={`gender-option ${catClone.gender === 'male' ? 'selected' : ''}`}>
                                <input type="radio" name="cat-clone-gender" value="male" checked={catClone.gender === 'male'} onChange={e => setCatClone(c => ({ ...c, gender: e.target.value }))} />
                                Male
                              </label>
                            </div>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 10, cursor: 'pointer' }}>
                            <input type="checkbox" checked={catClone.removeNoise}
                              onChange={e => setCatClone(c => ({ ...c, removeNoise: e.target.checked }))} />
                            Remove background noise from samples
                          </label>
                          <EmotionPanel
                            settings={previewSettings}
                            onChange={setPreviewSettings}
                            models={availableModels}
                          />
                        </div>
                      </div>
                    )}

                    {/* TAB 3: Upload pre-recorded audio files */}
                    {modalMode === 'add' && voiceTab === 'upload' && (
                      <div className="input-group" style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                        <label className="input-label">
                          <Upload size={14} style={{ marginRight: '6px' }} />
                          Upload audio files for this category
                        </label>
                        <div style={{ border: '1px solid rgba(34,197,94,0.35)', borderRadius: 8, padding: '12px 14px', background: 'rgba(34,197,94,0.05)' }}>
                          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                            Pick the voice set that will receive these files. No ElevenLabs generation. Recommended: 3 WAV files (1 per variation).
                          </div>

                          {/* Target voice set picker */}
                          <div className="input-group">
                            <label className="input-label">Target voice set *</label>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                              {[['existing', 'Add to existing voice set'], ['new', 'Create new voice set']].map(([key, lbl]) => (
                                <button key={key} type="button"
                                  onClick={() => setCatUpload(u => ({ ...u, mode: key }))}
                                  style={{
                                    flex: 1, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                                    fontSize: 12, fontWeight: 600,
                                    border: catUpload.mode === key ? '1px solid #22c55e' : '1px solid var(--border-color, #d1d5db)',
                                    background: catUpload.mode === key ? 'rgba(34,197,94,0.12)' : 'transparent',
                                    color: catUpload.mode === key ? '#22c55e' : 'inherit',
                                  }}>
                                  {lbl}
                                </button>
                              ))}
                            </div>
                            {catUpload.mode === 'existing' ? (
                              <select className="input"
                                value={catUpload.target_voice_set_id}
                                onChange={e => setCatUpload(u => ({ ...u, target_voice_set_id: e.target.value }))}>
                                <option value="">-- Pick a voice set --</option>
                                {voiceSets.map(vs => (
                                  <option key={vs.id} value={vs.id}>{vs.name}{vs.gender ? ` (${vs.gender})` : ''}</option>
                                ))}
                              </select>
                            ) : (
                              <input type="text" className="input"
                                value={catUpload.new_voice_set_name}
                                onChange={e => setCatUpload(u => ({ ...u, new_voice_set_name: e.target.value }))}
                                placeholder="e.g. Mike Voice" />
                            )}
                          </div>

                          <div className="input-group">
                            <label className="input-label"><Upload size={13} /> Audio Files *</label>
                            <input type="file" className="input" accept="audio/wav,audio/*" multiple
                              onChange={e => setCatUpload(u => ({ ...u, files: Array.from(e.target.files || []) }))} />
                            {catUpload.files.length > 0 && (
                              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
                                {catUpload.files.length} file(s): {catUpload.files.map(f => f.name).join(', ')}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 11, opacity: 0.6 }}>
                            Files are renamed to <code>{'{voiceset}_{category}_var{N}.wav'}</code> when saved.
                            If this category already exists for the chosen voice set, old files are replaced.
                          </div>
                        </div>
                      </div>
                    )}

                    {modalMode === 'add' && voiceTab !== 'upload' && stats.withElevenLabs > 0 && (
                      <div className="vs-info-box" style={{ marginTop: '12px' }}>
                        <Zap size={16} />
                        <span>Will generate for <strong>{stats.withElevenLabs}</strong> voice sets × <strong>{variations.filter(v => v.male?.trim()).length}</strong> variations = <strong>{stats.withElevenLabs * variations.filter(v => v.male?.trim()).length}</strong> audio files</span>
                      </div>
                    )}

                    {modalMode === 'add' && voiceTab !== 'upload' && previews.length === 0 && (
                      <div className="vs-warning-box">
                        <AlertTriangle size={16} />
                        <span>Tip: Test your variations first using a voice ID above before generating for all voice sets.</span>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                    {modalMode === 'add' && voiceTab === 'clone' ? (
                      <button className="btn btn-primary" onClick={createCategoryWithClone}
                        disabled={saving || !formData.name?.trim() || variations.filter(v => v.male?.trim()).length < 3 || !catClone.name.trim() || catClone.files.length === 0}>
                        {saving ? <Loader2 size={16} className="spin" /> : <Mic size={16} />} Clone &amp; Create
                      </button>
                    ) : modalMode === 'add' && voiceTab === 'upload' ? (
                      <button className="btn btn-primary" onClick={createCategoryWithUpload}
                        disabled={
                          saving ||
                          !formData.name?.trim() ||
                          catUpload.files.length === 0 ||
                          (catUpload.mode === 'existing' && !catUpload.target_voice_set_id) ||
                          (catUpload.mode === 'new' && !catUpload.new_voice_set_name?.trim())
                        }>
                        {saving ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} Upload &amp; Create
                      </button>
                    ) : (
                      <button className="btn btn-primary" onClick={saveGlobalCategory} disabled={saving || !formData.name?.trim() || variations.filter(v => v.male?.trim()).length < 3}>
                        {saving ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />} {modalMode === 'add' ? 'Create & Generate' : 'Update'}
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* LOCAL CATEGORY MODAL */}
              {modalType === 'category' && (
                <>
                  <div className="modal-header"><h2>{modalMode === 'add' ? 'Add' : 'Edit'} Category</h2><button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button></div>
                  <div className="modal-body">
                    <div className="input-group"><label className="input-label">Name</label><input type="text" className="input" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., greeting_audio" autoFocus /></div>
                    <div className="input-group"><label className="input-label">Description</label><input type="text" className="input" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" /></div>
                  </div>
                  <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button><button className="btn btn-primary" onClick={saveCategory} disabled={saving || !formData.name?.trim()}>{saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />} {modalMode === 'add' ? 'Create' : 'Update'}</button></div>
                </>
              )}

              {/* DELETE MODALS */}
              {(modalType === 'delete' || modalType === 'deleteCategory') && (
                <>
                  <div className="modal-header"><h2><Trash2 size={18} /> Delete {modalType === 'delete' ? 'Voice Set' : 'Category'}</h2><button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button></div>
                  <div className="modal-body"><p>Are you sure you want to delete "<strong>{currentItem?.name}</strong>"?</p><p className="vs-warning">⚠️ This will also delete all {modalType === 'delete' ? 'categories and audio files' : 'audio files'}!</p></div>
                  <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button><button className="btn btn-danger" onClick={modalType === 'delete' ? deleteVoiceSet : deleteCategory} disabled={saving}>{saving ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} Delete</button></div>
                </>
              )}

              {/* DELETE GLOBAL CATEGORY */}
              {modalType === 'deleteGlobalCategory' && (
                <>
                  <div className="modal-header"><h2><Trash2 size={18} /> Delete Global Category</h2><button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button></div>
                  <div className="modal-body">
                    <p>Are you sure you want to delete "<strong>{currentItem?.name}</strong>"?</p>
                    <p className="vs-warning">⚠️ This will delete this category from <strong>ALL voice sets</strong> and remove all associated audio files!</p>
                  </div>
                  <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button><button className="btn btn-danger" onClick={deleteGlobalCategory} disabled={saving}>{saving ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} Delete from All</button></div>
                </>
              )}

              {/* BULK UPLOAD MODAL */}
              {modalType === 'bulk' && (
                <>
                  <div className="modal-header vs-bulk-header"><h2><FolderPlus size={20} /> Bulk Upload Preview</h2>{!bulkUploading && <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>}</div>
                  <div className="modal-body">
                    {bulkUploading ? (
                      <div className="vs-upload-progress"><Loader2 size={48} className="spin" /><h4>Uploading...</h4><p>Please wait</p><div className="vs-progress-bar"><div className="vs-progress-fill" style={{ width: `${(bulkUploadProgress.processed / bulkUploadProgress.total) * 100}%` }} /></div></div>
                    ) : (
                      <>
                        <div className="vs-info-box"><strong>{bulkUploadProgress.categories}</strong> categories with <strong>{bulkUploadProgress.files}</strong> files</div>
                        <h4>Categories:</h4>
                        <div className="vs-categories-preview">{Object.keys(bulkUploadProgress.preview).sort().map(catName => <div key={catName} className="vs-preview-category"><div className="vs-preview-header"><span><Folder size={14} /> <strong>{catName}</strong> → <code>{convertToSnakeCase(catName)}</code></span><span className="vs-preview-badge">{bulkUploadProgress.preview[catName].length} files</span></div><div className="vs-preview-files">{bulkUploadProgress.preview[catName].slice(0, 3).join(', ')}{bulkUploadProgress.preview[catName].length > 3 && ` ... +${bulkUploadProgress.preview[catName].length - 3} more`}</div></div>)}</div>
                        <div className="vs-warning-box">⚠️ Files renamed to: <code>{getVoiceSetDisplayName()}_categoryname_var#.wav</code></div>
                      </>
                    )}
                  </div>
                  {!bulkUploading && <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button><button className="btn btn-success" onClick={startBulkUpload}><UploadCloud size={16} /> Upload All</button></div>}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Edit a clip's script (text only, or text + re-voice) ── */}
      {editScriptFile && (
        <div className="modal-overlay" onClick={() => !savingScript && setEditScriptFile(null)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Edit size={18} /> Edit Script</h2>
              <button className="modal-close" onClick={() => !savingScript && setEditScriptFile(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="vs-info-box"><AlertCircle size={16} /><span><strong>Save text</strong> updates the script only. <strong>Save &amp; Re-voice</strong> also regenerates this clip's audio in the set's cloned voice.</span></div>
              <div className="input-group" style={{ marginTop: 14 }}>
                <label className="input-label">Script text</label>
                <textarea className="input" rows={5} value={editScriptText} onChange={e => setEditScriptText(e.target.value)} placeholder="The line this clip speaks..." style={{ resize: 'vertical', minHeight: 110 }} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditScriptFile(null)} disabled={savingScript}>Cancel</button>
              <button className="btn btn-secondary" onClick={() => saveEditScript(false)} disabled={savingScript || !editScriptText.trim()}>
                {savingScript ? <Loader2 size={16} className="spin" /> : <Check size={16} />} Save text
              </button>
              {selectedVoiceSet?.minimax_voice_id && (
                <button className="btn btn-primary" onClick={() => saveEditScript(true)} disabled={savingScript || !editScriptText.trim()}>
                  {savingScript ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />} Save &amp; Re-voice
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MINIMAX: clone voice (permanent) + generate-from-script (appends) ── */}
      {mmModal && (
        <div className="modal-overlay" onClick={() => !mmBusy && setMmModal(null)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{mmModal.mode === 'clone'
                ? <><Mic size={18} /> {mmModal.vs.minimax_voice_id ? 'Replace' : 'Add'} Voice — {mmModal.vs.name}</>
                : <><Wand2 size={18} /> Generate from Script — {mmModal.vs.name}</>}</h2>
              <button className="modal-close" onClick={() => !mmBusy && setMmModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button className={`btn btn-xs ${mmModal.mode === 'clone' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMmModal(m => ({ ...m, mode: 'clone' }))}><Mic size={13} /> Voice</button>
                <button className={`btn btn-xs ${mmModal.mode === 'generate' ? 'btn-primary' : 'btn-secondary'}`} disabled={!mmModal.vs.minimax_voice_id} onClick={() => setMmModal(m => ({ ...m, mode: 'generate' }))}><Wand2 size={13} /> Generate</button>
              </div>

              {mmModal.mode === 'clone' ? (
                <>
                  <div className="vs-info-box"><AlertCircle size={16} /><span>Upload one clean sample (10–60s, single speaker, no music). It is cloned <strong>once</strong> and saved permanently to this voice set — you never upload it again.</span></div>
                  {mmModal.vs.minimax_voice_id && (
                    <div className="vs-warning-box" style={{ marginTop: 10 }}>⚠️ This set already has a permanent voice (<code>{mmModal.vs.minimax_voice_id}</code>). Re-cloning replaces it and uses another voice slot.&nbsp;
                      <label style={{ marginLeft: 6 }}><input type="checkbox" checked={mmReplace} onChange={e => setMmReplace(e.target.checked)} /> replace</label>
                    </div>
                  )}
                  <div className="input-group" style={{ marginTop: 14 }}>
                    <label className="input-label">Voice sample (mp3 / wav / m4a)</label>
                    <input className="input" type="file" accept="audio/*" onChange={e => setMmFile(e.target.files?.[0] || null)} style={{ padding: 8 }} />
                    {mmFile && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>{mmFile.name} ({Math.round(mmFile.size / 1024)} KB)</p>}
                  </div>
                </>
              ) : (
                !mmModal.vs.minimax_voice_id ? (
                  <div className="vs-warning-box">Add a voice first (Voice tab) — then you can generate.</div>
                ) : (
                  <>
                    <div className="vs-info-box"><AlertCircle size={16} /><span>Generates a WAV clip in this set's own voice and <strong>adds</strong> it to the category. Existing clips are never removed — generate as many lines as you want.</span></div>
                    <div className="input-group" style={{ marginTop: 14 }}>
                      <label className="input-label">Category</label>
                      <input className="input" placeholder="e.g. Greetings" value={mmCategory} onChange={e => setMmCategory(e.target.value)} />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Script text</label>
                      <textarea className="input" rows={4} placeholder="Type the line to speak..." value={mmText} onChange={e => setMmText(e.target.value)} style={{ resize: 'vertical', minHeight: 90 }} />
                    </div>
                    {mmResults.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Added this session ({mmResults.length}):</div>
                        {mmResults.map((r, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.8rem', padding: '5px 0', borderTop: '1px solid var(--border-color)' }}>
                            <span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span>
                            <span style={{ flex: 1 }}><strong>{r.category}</strong>: "{r.text.length > 48 ? r.text.slice(0, 48) + '…' : r.text}"</span>
                            <span style={{ color: 'var(--text-muted)' }}>{(r.duration_ms / 1000).toFixed(1)}s</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setMmModal(null)} disabled={mmBusy}>Close</button>
              {mmModal.mode === 'clone'
                ? <button className="btn btn-primary" onClick={submitMMClone} disabled={mmBusy || !mmFile}>{mmBusy ? <Loader2 size={16} className="spin" /> : <Mic size={16} />} {mmModal.vs.minimax_voice_id ? 'Replace Voice' : 'Clone & Save Voice'}</button>
                : <button className="btn btn-primary" onClick={submitMMGenerate} disabled={mmBusy || !mmModal.vs.minimax_voice_id}>{mmBusy ? <Loader2 size={16} className="spin" /> : <Wand2 size={16} />} Generate &amp; Add Clip</button>}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

export default VoiceSets